import { DatePipe } from '@angular/common';
import { Component, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import * as _moment from 'moment';
import { default as _rollupMoment } from 'moment';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, finalize, timeout } from 'rxjs/operators';
import Swal from 'sweetalert2';

const moment = _rollupMoment || _moment;

// services
import { InvoiceService } from '../../services/invoice.service';
import { PatientService } from '../../services/patient.service';
import { PaymentLinksService } from '../../services/payment-links.service';
import { ReportService } from '../../services/report.service';
import { ResidentService } from '../../services/resident.service';
import { SettingsService } from '../../services/settings.service';
import { SharedService } from '../../services/shared.service';
import { formatOopsErrorMessage } from '../../shared-data/helper-functions';
// interfaces
import { IInvoiceNewVo } from '../../interfaces/Invoice/iinvoice-new-vo';
import { ISageBalanceForwardDto } from '../../interfaces/Sage/isage-balance-forward-dto';
import { ISageCustomerDto } from '../../interfaces/Sage/isage-customer-dto';
import { ISageCustomerSearchRequestDto } from '../../interfaces/Sage/isage-customer-search-request-dto';
import { IResultObjVo } from '../../interfaces/iresult-obj-vo';
import { IUserActionLogRowVo } from '../../interfaces/Invoice/iuser-action-log-row-vo';
// shared data
import { INVOICE_REVIEW_STATUS } from '../../shared-data/invoice-review-status';
import { INVOICE_SAGE_STATUS } from '../../shared-data/invoice-sage-status';

@Component({
  selector: 'app-invoice-profile-details',
  templateUrl: './invoice-profile-details.component.html',
  styleUrls: ['./invoice-profile-details.component.css']
})
export class InvoiceProfileDetailsComponent implements OnInit, OnDestroy {

  invoiceList: IInvoiceNewVo[] = [];
  sageBalanceForward: ISageBalanceForwardDto = {
    customer: {
      id: 0,
      pharmacyId: 0,
      facilityId: 0,
      patientId: 0,
      sageName: '',
      name: '',
      email: '',
      accountNumber: '',
      contact: '',
      street1: '',
      street2: '',
      city: '',
      province: '',
      country: '',
      postalCode: '',
      paymentType: '',
      status: false,
      canSendEmail: false,
      sageCustomerId: null,
      phone1: '',
      phone2: ''
    },
    transactionList: [],
    balanceForward: 0
  };

  pharmacyId: number;
  facilityId: number;
  patientId: number;

  activeProfileTab = 'sage-profile';
  paymentVerifyLinkLoading = false;
  paymentVerifyLinkEmailLoading = false;
  paymentVerifyLinkSmsLoading = false;

  showLoadingMessage: boolean;
  showErrorMessage: boolean = false;
  errorMessage: string = '';

  displaySuccessMessage: boolean = false;
  successMessage: string = '';

  reviewStatus = INVOICE_REVIEW_STATUS;
  sageStatus = INVOICE_SAGE_STATUS;

  // Sorting
  sortedData: any[] = [];
  currentSortField: string = '';
  sortDirection: 'asc' | 'desc' = 'desc';

  // Balance forward: year (Fill Month), transaction type, detail type (null = All)
  balanceForwardSelectedYear: number | null = null;
  balanceForwardSelectedTransactionType: string | null = null;
  balanceForwardSelectedDetailType: string | null = null;
  /** Row keys excluded from balance forward totals (click row to toggle). */
  balanceForwardExcludedKeys: { [key: string]: boolean } = {};

  // Profile data for modal editing
  profileData = {
    sageName: '',
    sageCustomerId: null as number | null,
    contact: '',
    email: '',
    accountNumber: '',
    paymentType: '',
    street1: '',
    street2: '',
    city: '',
    province: '',
    postalCode: '',
    country: 'Canada',
    canSendEmail: true,
    phone1: '',
    phone2: ''
  };

  // Validation flags
  emailInvalid = false;
  postalCodeInvalid = false;

  // Sage Customer search
  sageSearchTerm = '';
  sageSearchResults: ISageCustomerDto[] = [];
  sageSearching = false;
  private sageSearchSubject = new Subject<string>();
  private sageSearchSubscription: any;
  selectedTaxYear: number | null = null;

  annualInvoiceItems: any[] = [];
  annualInvoiceTotalPays: number = 0;
  annualInvoiceTotalHst: number = 0;
  annualInvoiceTotalInvoice: number = 0;
  annualInvoiceLoading: boolean = false;
  annualInvoiceDateBreak: boolean[] = [];
  annualInvoiceDateEnd: boolean[] = [];
  annualInvoiceMonthlySummary: any[] = [];

  showAnnualScrollTopButton: boolean = false;
  showAnnualScrollBottomButton: boolean = false;

  showAnnualTaxReceiptActionLogPanel = true;
  annualTaxReceiptActionLogs: IUserActionLogRowVo[] = [];
  annualTaxReceiptActionLogLoading = false;
  annualTaxReceiptActionLogError = '';

  paymentSetupLinkActionLogs: IUserActionLogRowVo[] = [];
  paymentSetupLinkActionLogLoading = false;
  paymentSetupLinkActionLogError = '';

  paymentMethodSetupCodes: any[] = [];
  paymentMethodSetupCodesLoading = false;
  paymentMethodSetupCodesError = '';
  paymentMethodSetupCodeCancelingCode = '';
  paymentSetupUnmatchedActionLogs: IUserActionLogRowVo[] = [];

  paymentLinkShareLogs: IUserActionLogRowVo[] = [];
  paymentActionLogs: IUserActionLogRowVo[] = [];
  showPaymentActionLogPanel = true;

  readonly paymentLogPageSize = 5;
  paymentSetupCodesPage = 1;
  paymentSetupCodesSortField = 'createdAt';
  paymentSetupCodesSortDir: 'asc' | 'desc' = 'desc';
  paymentActivityPage = 1;
  paymentActivitySortField = 'createdDate';
  paymentActivitySortDir: 'asc' | 'desc' = 'desc';

  patientPaymentMethods: any = null;
  patientPaymentMethodsLoading = false;
  patientPaymentMethodsError = '';
  paymentMethodDeletingKey = '';
  paymentPadLoadingKey = '';
  showInactivePaymentCards = false;
  showInactivePaymentBankAccounts = false;

  /** Patient clinical banner (photo, ID, vitals, diet) — from Connect DTO + local resident profile */
  patientDto: any = {};
  patientVo: any = {};
  patientClinicalBannerReady = false;
  patientProfileImageUrl = 'assets/img/default-avatar.png';
  readonly patientProfileImageFallback = 'assets/img/default-avatar.png';
  private patientProfileImageSettings: { hashInputSalt: string; fileservicePatientsBaseUrl: string };

  get taxReceiptPatientNameLastFirst(): string {
    const fullName =
      this.invoiceList && this.invoiceList.length > 0 && this.invoiceList[0] && this.invoiceList[0].billedTo
        ? this.invoiceList[0].billedTo
        : '';

    return fullName;
  }

  get taxReceiptFacilityName(): string {
    return this.invoiceList && this.invoiceList.length > 0 && this.invoiceList[0] && this.invoiceList[0].facilityName
      ? this.invoiceList[0].facilityName
      : '';
  }

  get paymentPatientPharmacyName(): string {
    const name = this.sharedService && this.sharedService.pharmacy
      ? String(this.sharedService.pharmacy).trim()
      : '';
    return name ? name : '—';
  }

  get paymentPatientFacilityName(): string {
    if (this.taxReceiptFacilityName) {
      return this.taxReceiptFacilityName;
    }
    const fromShared = this.sharedService && this.sharedService.facilityName
      ? String(this.sharedService.facilityName).trim()
      : '';
    return fromShared ? fromShared : '—';
  }

  get taxReceiptStreetLine(): string {
    const c = this.sageBalanceForward && this.sageBalanceForward.customer ? this.sageBalanceForward.customer : null;
    if (!c) return '';

    const street1 = c.street1 ? String(c.street1).trim() : '';
    const street2 = c.street2 ? String(c.street2).trim() : '';

    if (street2 && street1) return `${street2} ${street1}`;
    return street2 || street1 || '';
  }

  get taxReceiptCityProvinceLine(): string {
    const c = this.sageBalanceForward && this.sageBalanceForward.customer ? this.sageBalanceForward.customer : null;
    if (!c) return '';

    const city = c.city ? String(c.city).trim() : '';
    const province = c.province ? String(c.province).trim() : '';

    if (city && province) return `${city}, ${province}`;
    return city || province || '';
  }

  get taxReceiptPostalCode(): string {
    const c = this.sageBalanceForward && this.sageBalanceForward.customer ? this.sageBalanceForward.customer : null;
    return this.formatCanadianPostalCode(c && c.postalCode ? String(c.postalCode) : '');
  }

  private balanceForwardRawTransactionList(): any[] {
    return (this.sageBalanceForward && this.sageBalanceForward.transactionList)
      ? this.sageBalanceForward.transactionList
      : [];
  }

  /** After year filter only (Fill Month year) */
  get balanceForwardTransactionsAfterYear(): any[] {
    const list = this.balanceForwardRawTransactionList();
    if (this.balanceForwardSelectedYear == null) {
      return list;
    }
    const year = this.balanceForwardSelectedYear;
    return list.filter(t => this.getFillDateRangeYear(t.transactionDate) === year);
  }

  /** Distinct transactionTypeName in current year scope (Invoice, Deposit, Credit note, etc.) */
  get balanceForwardAvailableTransactionTypes(): string[] {
    const set = new Set<string>();
    this.balanceForwardTransactionsAfterYear.forEach(t => {
      const n = (t.transactionTypeName != null ? String(t.transactionTypeName) : '').trim();
      if (n) {
        set.add(n);
      }
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }

  /** After year + transaction type */
  get balanceForwardTransactionsAfterYearAndTxType(): any[] {
    let out = this.balanceForwardTransactionsAfterYear;
    if (this.balanceForwardSelectedTransactionType) {
      const sel = this.balanceForwardSelectedTransactionType;
      out = out.filter(t => (t.transactionTypeName != null ? String(t.transactionTypeName) : '').trim() === sel);
    }
    return out;
  }

  /** Distinct transactDetailsTypeName for rows matching year + transaction type */
  get balanceForwardAvailableDetailTypes(): string[] {
    const set = new Set<string>();
    this.balanceForwardTransactionsAfterYearAndTxType.forEach(t => {
      const n = (t.transactDetailsTypeName != null ? String(t.transactDetailsTypeName) : '').trim();
      if (n) {
        set.add(n);
      }
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }

  /** Filtered table rows */
  get balanceForwardFilteredTransactionList(): any[] {
    let out = this.balanceForwardTransactionsAfterYearAndTxType;
    if (this.balanceForwardSelectedDetailType) {
      const sel = this.balanceForwardSelectedDetailType;
      out = out.filter(t => (t.transactDetailsTypeName != null ? String(t.transactDetailsTypeName) : '').trim() === sel);
    }
    return out;
  }

  /** Filtered rows still included in footer totals */
  get balanceForwardTotalsTransactionList(): any[] {
    const self = this;
    return this.balanceForwardFilteredTransactionList.filter(function (t, i) {
      return !self.isBalanceForwardRowExcluded(t, i);
    });
  }

  get balanceForwardExcludedCount(): number {
    return Object.keys(this.balanceForwardExcludedKeys).length;
  }

  pruneBalanceForwardFilters(): void {
    const tx = this.balanceForwardAvailableTransactionTypes;
    if (this.balanceForwardSelectedTransactionType && tx.indexOf(this.balanceForwardSelectedTransactionType) < 0) {
      this.balanceForwardSelectedTransactionType = null;
    }
    const dt = this.balanceForwardAvailableDetailTypes;
    if (this.balanceForwardSelectedDetailType && dt.indexOf(this.balanceForwardSelectedDetailType) < 0) {
      this.balanceForwardSelectedDetailType = null;
    }
  }

  onBalanceForwardYearChange(year: number | null): void {
    this.balanceForwardSelectedYear = year;
    this.clearBalanceForwardExcludedRows();
    this.pruneBalanceForwardFilters();
  }

  onBalanceForwardTransactionTypeChange(value: string | null): void {
    this.balanceForwardSelectedTransactionType = value;
    this.clearBalanceForwardExcludedRows();
    const dt = this.balanceForwardAvailableDetailTypes;
    if (this.balanceForwardSelectedDetailType && dt.indexOf(this.balanceForwardSelectedDetailType) < 0) {
      this.balanceForwardSelectedDetailType = null;
    }
  }

  onBalanceForwardDetailTypeChange(value: string | null): void {
    this.balanceForwardSelectedDetailType = value;
    this.clearBalanceForwardExcludedRows();
  }

  clearBalanceForwardExcludedRows(): void {
    this.balanceForwardExcludedKeys = {};
  }

  isBalanceForwardRowExcluded(transaction: any, index: number): boolean {
    return !!this.balanceForwardExcludedKeys[this.getBalanceForwardTransactionKey(transaction, index)];
  }

  toggleBalanceForwardRowExcluded(transaction: any, index: number): void {
    const key = this.getBalanceForwardTransactionKey(transaction, index);
    if (this.balanceForwardExcludedKeys[key]) {
      delete this.balanceForwardExcludedKeys[key];
    } else {
      this.balanceForwardExcludedKeys[key] = true;
    }
  }

  private getBalanceForwardTransactionKey(transaction: any, index: number): string {
    if (transaction && transaction.transactionId != null && !isNaN(Number(transaction.transactionId))) {
      return 'id:' + String(transaction.transactionId);
    }
    const date = transaction && transaction.transactionDate != null ? String(transaction.transactionDate) : '';
    const source = transaction && transaction.source != null ? String(transaction.source) : '';
    const amount = transaction && transaction.amount != null ? String(transaction.amount) : '';
    return 'row:' + date + '|' + source + '|' + amount + '|' + String(index);
  }

  /** Years (by Fill Month) that have at least one transaction; else [currentYear .. 2020] */
  get balanceForwardAvailableYears(): number[] {
    const list = (this.sageBalanceForward && this.sageBalanceForward.transactionList) ? this.sageBalanceForward.transactionList : [];
    const currentYear = new Date().getFullYear();
    if (list.length === 0) {
      const years: number[] = [];
      for (let y = currentYear; y >= 2020; y--) years.push(y);
      return years;
    }
    const set = new Set<number>();
    list.forEach(t => {
      const y = this.getFillDateRangeYear(t.transactionDate);
      if (!isNaN(y)) set.add(y);
    });
    return Array.from(set).sort((a, b) => b - a);
  }

  get balanceInvoiceTotal(): number {
    const list = this.balanceForwardTotalsTransactionList;
    return list.filter(t => t.amount != null && t.amount > 0).reduce((sum, t) => sum + Number(t.amount), 0);
  }

  get balancePreTaxTotal(): number {
    const list = this.balanceForwardTotalsTransactionList;
    return list
      .filter(t => t.transactDetailsType === 0 && t.preTaxAmount != null && !isNaN(Number(t.preTaxAmount)))
      .reduce((sum, t) => sum + Number(t.preTaxAmount), 0);
  }

  get balanceTaxTotal(): number {
    const list = this.balanceForwardTotalsTransactionList;
    return list
      .filter(t => t.transactDetailsType === 0 && t.taxAmount != null && !isNaN(Number(t.taxAmount)))
      .reduce((sum, t) => sum + Number(t.taxAmount), 0);
  }

  get balancePaymentTotal(): number {
    const list = this.balanceForwardTotalsTransactionList;
    return list.filter(t => t.amount != null && t.amount < 0).reduce((sum, t) => sum + Number(t.amount), 0);
  }

  /** Total row: overall balance when All year and All type, else sum of filtered amounts */
  get balanceForwardDisplayTotal(): number {
    if (this.balanceForwardExcludedCount === 0
        && this.balanceForwardSelectedYear == null
        && !this.balanceForwardSelectedTransactionType
        && !this.balanceForwardSelectedDetailType
        && this.sageBalanceForward && this.sageBalanceForward.balanceForward != null) {
      return this.sageBalanceForward.balanceForward;
    }
    return this.balanceInvoiceTotal + this.balancePaymentTotal;
  }

  /** Year of (transaction date minus one month) for Fill Month–based filtering */
  getFillDateRangeYear(transactionDate: string | null): number {
    if (!transactionDate) return NaN;
    const d = new Date(transactionDate);
    if (isNaN(d.getTime())) return NaN;
    d.setMonth(d.getMonth() - 1);
    return d.getFullYear();
  }

  /** When detail type is Unknown, show "TransactionType (Unknown)" e.g. Deposit (Unknown) */
  getBalanceForwardDetailTypeDisplay(transaction: any): string {
    const detail = (transaction.transactDetailsTypeName != null ? String(transaction.transactDetailsTypeName) : '').trim();
    if (detail.toLowerCase() === 'unknown') {
      const tx = (transaction.transactionTypeName != null ? String(transaction.transactionTypeName) : '').trim();
      return tx ? `${tx} (Unknown)` : 'Unknown';
    }
    return detail;
  }

  /** Transaction date minus one month as yyyy-MM for Fill Month column */
  getFillDateRangeMonth(transactionDate: string | null): string {
    if (!transactionDate) return '';
    const d = new Date(transactionDate);
    if (isNaN(d.getTime())) return '';
    d.setMonth(d.getMonth() - 1);
    const y = d.getFullYear();
    const m = d.getMonth() + 1;
    return y + '-' + (m < 10 ? '0' : '') + m;
  }

  private formatCanadianPostalCode(postalCode: string): string {
    if (!postalCode) return '';
    const raw = String(postalCode).toUpperCase().replace(/\s+/g, '').trim();
    if (raw.length === 6) {
      return `${raw.substring(0, 3)} ${raw.substring(3)}`;
    }
    return raw;
  }

  private buildAnnualTaxReceiptFileBaseName(year: number): string {
    const fullName = this.taxReceiptPatientNameLastFirst || '';
    const parsed = this.parseLastFirstNameForFileName(fullName);
    return `Annual-Tax-Receipt-${year}-${parsed.lastName}-${parsed.firstName}`;
  }

  private parseLastFirstNameForFileName(fullName: string): { lastName: string; firstName: string } {
    const cleaned = String(fullName || '').trim().replace(/\s+/g, ' ');

    let last = '';
    let first = '';

    if (cleaned.indexOf(',') >= 0) {
      const parts = cleaned.split(',');
      last = parts[0] ? parts[0].trim() : '';
      first = parts[1] ? parts[1].trim() : '';
    } else {
      const parts = cleaned.split(' ').filter(x => x);
      if (parts.length >= 2) {
        last = parts[parts.length - 1];
        first = parts.slice(0, parts.length - 1).join(' ');
      } else if (parts.length === 1) {
        last = parts[0];
      }
    }

    const lastName = this.sanitizeFileNameSegment(last) || 'LastName';
    const firstName = this.sanitizeFileNameSegment(first) || 'FirstName';

    return { lastName, firstName };
  }

  private sanitizeFileNameSegment(value: string): string {
    return String(value || '').replace(/[^a-zA-Z0-9]+/g, '').trim();
  }

  private getInvoiceRecipientEmail(): string {
    const c = this.sageBalanceForward && this.sageBalanceForward.customer ? this.sageBalanceForward.customer : null;
    const email = c && c.email ? String(c.email).trim() : '';
    return email || '';
  }

  private async getLoggedInUserEmail(): Promise<string> {
    const userId = this.sharedService ? this.sharedService.getUserId() : null;
    if (!userId) return '';

    return new Promise<string>(resolve => {
      try {
        this.settingsService.getUserProfile(userId).subscribe(data => {
          const email = data && data.email ? String(data.email).trim() : '';
          resolve(email);
        }, _err => resolve(''));
      } catch (_e) {
        resolve('');
      }
    });
  }

  private isValidEmail(email: string): boolean {
    if (!email) return false;
    const e = String(email).trim();
    // Simple email validation (good enough for UI selection)
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
  }

  private isValidPhone(phone: string): boolean {
    return !!this.normalizePhoneToE164(phone);
  }

  private sanitizeNationalPhoneInput(phone: string): string {
    if (!phone) {
      return '';
    }
    return String(phone).replace(/[^\d\s().-]/g, '');
  }

  // Custom phone UI omits +1. Prepend +1, then validate as E.164.
  private normalizeCustomNationalPhoneToE164(nationalPhone: string): string {
    var digits = String(nationalPhone || '').replace(/\D/g, '');
    if (!digits) {
      return '';
    }
    if (digits.length === 11 && digits.charAt(0) === '1') {
      return this.normalizePhoneToE164('+' + digits);
    }
    return this.normalizePhoneToE164('+1' + digits);
  }

  // E.164: + and 8-15 digits. 10-digit NANP numbers are stored as +1XXXXXXXXXX.
  private normalizePhoneToE164(phone: string): string {
    if (!phone) {
      return '';
    }
    var raw = String(phone).trim();
    var hasPlus = raw.charAt(0) === '+';
    var digits = raw.replace(/\D/g, '');
    if (!digits) {
      return '';
    }
    if (digits.length === 11 && digits.charAt(0) === '1') {
      return '+' + digits;
    }
    if (digits.length === 10) {
      return '+1' + digits;
    }
    if (hasPlus && digits.length >= 8 && digits.length <= 15 && digits.charAt(0) !== '0') {
      return '+' + digits;
    }
    return '';
  }

  exportAnnualTaxReceiptPdf() {
    if (this.annualInvoiceLoading) return;

    if (!this.annualInvoiceMonthlySummary || this.annualInvoiceMonthlySummary.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'No data to export',
        text: 'Please select a year with invoice data before downloading the Annual Tax Receipt.',
        confirmButtonText: 'OK'
      });
      return;
    }

    Swal.fire({
      icon: 'question',
      title: 'Download Annual Tax Receipt PDF?',
      text: 'Do you want to generate and download the Annual Tax Receipt as a PDF file?',
      showCancelButton: true,
      cancelButtonText: 'Cancel',
      confirmButtonText: 'Yes, download'
    }).then(result => {
      if (!result.isConfirmed) return;

      this.downloadAnnualTaxReceiptPdf();
    });
  }

  private downloadAnnualTaxReceiptPdf() {
    const year = this.selectedTaxYear;
    if (year == null) return;

    const contentEl = document.getElementById('annual-tax-receipt-content');
    const html = contentEl ? String(contentEl.innerHTML) : '';

    // Preferred path: backend generates PDF (Telerik). If not available yet, fall back to Print-to-PDF.
    this.showLoadingMessage = true;
    this.reportService.getAnnualTaxReceiptPdf({
      pharmacyId: this.pharmacyId,
      facilityId: this.facilityId,
      patientId: this.patientId,
      year,
      html,
      performedByUserId: this.sharedService.getUserId()
    })
      .pipe(timeout(6000000))
      .subscribe(blob => {
        if (!blob) {
          this.showLoadingMessage = false;
          this.printAnnualTaxReceiptToPdf(year);
          return;
        }

        const fileUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = fileUrl;

        const now = new Date();
        const formattedDate = now.getFullYear() +
          String(now.getMonth() + 1).padStart(2, '0') +
          String(now.getDate()).padStart(2, '0') +
          String(now.getHours()).padStart(2, '0') +
          String(now.getMinutes()).padStart(2, '0') +
          String(now.getMilliseconds()).padStart(2, '0');

        // Note: For server-generated PDFs we can set the filename directly.
        a.download = `${this.buildAnnualTaxReceiptFileBaseName(year)}.pdf`;
        a.click();
        window.URL.revokeObjectURL(fileUrl);
        this.showLoadingMessage = false;
      }, _error => {
        this.showLoadingMessage = false;
        this.printAnnualTaxReceiptToPdf(year);
      });
  }

  private printAnnualTaxReceiptToPdf(year: number) {
    const contentEl = document.getElementById('annual-tax-receipt-content');
    if (!contentEl) {
      Swal.fire({
        icon: 'error',
        title: 'Unable to export',
        text: 'Annual Tax Receipt content was not found on the page.',
        confirmButtonText: 'OK'
      });
      return;
    }

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      Swal.fire({
        icon: 'warning',
        title: 'Popup blocked',
        text: 'Please allow popups to export the Annual Tax Receipt.',
        confirmButtonText: 'OK'
      });
      return;
    }

    const title = this.buildAnnualTaxReceiptFileBaseName(year);
    // Build print-only HTML (no UI changes): remove monthly table and show only invoice total.
    const cloned = contentEl.cloneNode(true) as HTMLElement;

    const monthlyTable = cloned.querySelector('.annual-invoice-monthly-table');
    if (monthlyTable) {
      const wrapper = monthlyTable.closest('.table-responsive');
      if (wrapper && wrapper.parentNode) {
        wrapper.parentNode.removeChild(wrapper);
      } else if (monthlyTable.parentNode) {
        monthlyTable.parentNode.removeChild(monthlyTable);
      }
    }

    const total = typeof this.annualInvoiceTotalInvoice === 'number' && !isNaN(this.annualInvoiceTotalInvoice)
      ? this.annualInvoiceTotalInvoice
      : 0;

    const totalLine = document.createElement('div');
    totalLine.className = 'annual-total-paid-line';
    totalLine.innerHTML = `
      <span class="label">Total Amount Paid by the Patient</span>
      <span class="amount-wrap">
        <span class="amount">$ ${total.toFixed(2)}</span>
      </span>
    `;
    cloned.appendChild(totalLine);

    // Keep wrapper id for scoped print CSS rules
    const contentHtml = cloned.outerHTML;

    printWindow.document.open();
    printWindow.document.write(`
      <!doctype html>
      <html>
        <head>
          <meta charset="utf-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <title>${title}</title>
          <style>
            @page { margin: 0; }
            body { font-family: Arial, Helvetica, sans-serif; color: #212529; margin: 1.5cm; }
            .text-center { text-align: center; }
            .text-end { text-align: right; }
            .text-start { text-align: left; }
            .fw-bold { font-weight: 700; }
            /* PDF sizing adjustments */
            #annual-tax-receipt-content { font-size: 0.78rem; }
            #annual-tax-receipt-content .fs-4 { font-size: 1.0rem; } /* title: 2 steps smaller */
            /* Personal info lines (pharmacy/phone) should match patient info font-size */
            #annual-tax-receipt-content .fs-5 { font-size: inherit; }
            .text-decoration-underline { text-decoration: underline; }
            /* Add extra blank lines under the title (PDF only) */
            #annual-tax-receipt-content .text-center.fw-bold.fs-4 { margin-bottom: 26px; }
            table { width: 100%; border-collapse: collapse; margin-top: 16px; }
            #annual-tax-receipt-content table th,
            #annual-tax-receipt-content table td { font-size: 0.70rem; padding: 5px; }

            /* Repeat the detail table header on each printed page */
            #annual-tax-receipt-content .annual-invoice-detail-table thead { display: table-header-group; }

            /* Detail table alignment: left for first 4 columns */
            #annual-tax-receipt-content .annual-invoice-detail-table th:nth-child(-n+4),
            #annual-tax-receipt-content .annual-invoice-detail-table td:nth-child(-n+4) {
              text-align: left !important;
            }

            /* Preserve right alignment for currency column */
            #annual-tax-receipt-content .annual-invoice-detail-table th.text-end,
            #annual-tax-receipt-content .annual-invoice-detail-table td.text-end {
              text-align: right !important;
            }

            /* Detail table: show only header bottom + table bottom border */
            #annual-tax-receipt-content .annual-invoice-detail-table th,
            #annual-tax-receipt-content .annual-invoice-detail-table td {
              border: none !important;
            }

            #annual-tax-receipt-content .annual-invoice-detail-table thead th {
              border-bottom: 1px solid #212529 !important;
              background: #ffffff;
            }

            /* Ensure underline prints on repeated headers (page 2+) */
            #annual-tax-receipt-content .annual-invoice-detail-table thead,
            #annual-tax-receipt-content .annual-invoice-detail-table thead tr {
              border-bottom: 1px solid #212529 !important;
            }

            #annual-tax-receipt-content .annual-invoice-detail-table tr.annual-invoice-last-row td {
              border-bottom: 1px solid #212529 !important;
            }

            #annual-tax-receipt-content .annual-total-paid-line {
              margin-top: 18px;
              font-size: 0.70rem;
              display: flex;
              justify-content: flex-end;
              align-items: flex-end;
              gap: 24px;
            }

            #annual-tax-receipt-content .annual-total-paid-line .label {
              font-weight: 700;
              display: inline-block;
              border-bottom: 1px solid #212529;
              padding-bottom: 2px;
              line-height: 1.1;
            }

            #annual-tax-receipt-content .annual-total-paid-line .amount-wrap {
              display: inline-flex;
              flex-direction: column;
              align-items: flex-end;
              text-align: right;
              min-width: 160px;
            }

            #annual-tax-receipt-content .annual-total-paid-line .amount {
              font-weight: 700;
              line-height: 1.1;
              white-space: nowrap;
              display: inline-block;
              border-bottom: 1px solid #212529;
              padding-bottom: 2px;
            }

            #annual-tax-receipt-content .annual-total-paid-line .blank {
              display: inline-block;
              border-bottom: 1px solid #212529;
              padding-bottom: 2px;
              line-height: 1.1;
              white-space: nowrap;
            }

            @media print { a[href]:after { content: ""; } }
          </style>
        </head>
        <body>
          ${contentHtml}
        </body>
      </html>
    `);
    printWindow.document.close();

    // Give the browser a moment to render before printing.
    setTimeout(() => {
      try {
        printWindow.focus();
        printWindow.print();
      } catch (_e) { }
    }, 300);

    Swal.fire({
      icon: 'info',
      title: 'Print to PDF',
      text: 'If the server PDF export is not implemented yet, you can save this report using the Print dialog (Save as PDF).',
      confirmButtonText: 'OK'
    });
  }

  async sendAnnualTaxReceiptEmail() {
    if (this.annualInvoiceLoading) return;

    if (!this.annualInvoiceMonthlySummary || this.annualInvoiceMonthlySummary.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'No data to send',
        text: 'Please select a year with invoice data before sending the Annual Tax Receipt.',
        confirmButtonText: 'OK'
      });
      return;
    }

    const invoiceEmail = this.getInvoiceRecipientEmail();
    const userEmail = await this.getLoggedInUserEmail();

    const invoiceRow = invoiceEmail ? invoiceEmail : '<span class="text-muted">(No email found)</span>';
    const userRow = userEmail ? userEmail : '<span class="text-muted">(No email found)</span>';

    const year = this.selectedTaxYear;
    if (year == null) return;

    const fileBaseName = this.buildAnnualTaxReceiptFileBaseName(year);
    const subjectDefault = `Annual Tax Receipt – ${year} | London Medical Pharmacy`;
    const bodyDefault =
      `Good day,\n\n` +
      `Please find attached the annual tax receipt for the ${year} tax year.\n\n` +
      `Resident: ${this.taxReceiptPatientNameLastFirst}\n` +
      (this.taxReceiptFacilityName ? `Facility: ${this.taxReceiptFacilityName}\n` : ``) +
      `Total amount paid (CAD): $${(this.annualInvoiceTotalInvoice || 0).toFixed(2)}\n\n` +
      `Please retain this receipt for your records. It may be used to support eligible ` +
      `medical expense claims on a Canadian income tax return, where applicable.\n\n` +
      `Should you have any questions, please contact our Finance Department toll-free ` +
      `anywhere in Canada:\n` +
      `1-844-519-4949, ext. 4\n\n` +
      `Sincerely,\n\n` +
      `Finance Department\n` +
      `London Medical Pharmacy\n` +
      `London, ON\n` +
      `Canada`
      ;

    Swal.fire({
      icon: 'question',
      title: 'Send Annual Tax Receipt by E-MAIL?',
      width: 800,
      html: `
        <div style="text-align:left;">
          <div class="mb-2 fw-bold">Select recipients</div>

          <div class="form-check mb-2">
            <input class="form-check-input" type="checkbox" id="tax-email-invoice" ${invoiceEmail ? '' : 'disabled'}>
            <label class="form-check-label" for="tax-email-invoice">Invoice recipient email</label>
            <div class="text-muted swal-recipient-email" style="margin-left: 24px;">${invoiceRow}</div>
          </div>

          <div class="form-check mb-2">
            <input class="form-check-input" type="checkbox" id="tax-email-user" ${userEmail ? '' : 'disabled'}>
            <label class="form-check-label" for="tax-email-user">Current logged-in user email</label>
            <div class="text-muted swal-recipient-email" style="margin-left: 24px;">${userRow}</div>
          </div>

          <div class="form-check mb-2">
            <input class="form-check-input" type="checkbox" id="tax-email-custom-check">
            <label class="form-check-label" for="tax-email-custom-check">Custom email</label>
          </div>
          <input id="tax-email-custom-input" class="swal2-input" placeholder="Enter email address" style="margin: 6px 0 0 0; width: 100%;" />

          <hr class="my-3" />
          <div class="mb-2 fw-bold">Email content</div>
          <input id="tax-email-subject" class="swal2-input" placeholder="Subject" style="margin: 6px 0 0 0; width: 100%;" value="${subjectDefault.replace(/"/g, '&quot;')}" />
          <textarea id="tax-email-body" class="swal2-textarea" placeholder="Email body" rows="10" style="margin: 6px 0 0 0; width: 100%; height: auto;">${bodyDefault}</textarea>
        </div>
      `,
      showCancelButton: true,
      cancelButtonText: 'Cancel',
      confirmButtonText: 'Yes, send',
      preConfirm: () => {
        const inv = document.getElementById('tax-email-invoice') as any;
        const usr = document.getElementById('tax-email-user') as any;
        const customCheck = document.getElementById('tax-email-custom-check') as any;
        const customInput = document.getElementById('tax-email-custom-input') as any;
        const subjectEl = document.getElementById('tax-email-subject') as any;
        const bodyEl = document.getElementById('tax-email-body') as any;

        const emails: string[] = [];

        if (inv && inv.checked) {
          if (!invoiceEmail || !this.isValidEmail(invoiceEmail)) {
            Swal.showValidationMessage('Invoice recipient email is missing or invalid.');
            return null;
          }
          emails.push(invoiceEmail);
        }

        if (usr && usr.checked) {
          if (!userEmail || !this.isValidEmail(userEmail)) {
            Swal.showValidationMessage('Current user email is missing or invalid.');
            return null;
          }
          emails.push(userEmail);
        }

        if (customCheck && customCheck.checked) {
          const customVal = customInput && customInput.value ? String(customInput.value).trim() : '';
          if (!customVal) {
            Swal.showValidationMessage('Please enter a custom email address.');
            return null;
          }
          if (!this.isValidEmail(customVal)) {
            Swal.showValidationMessage('Custom email address is invalid.');
            return null;
          }
          emails.push(customVal);
        }

        const unique = Array.from(new Set(emails.filter(x => x)));
        if (unique.length === 0) {
          Swal.showValidationMessage('Please select at least one recipient.');
          return null;
        }

        const subject = subjectEl && subjectEl.value ? String(subjectEl.value).trim() : '';
        const body = bodyEl && bodyEl.value ? String(bodyEl.value) : '';

        if (!subject) {
          Swal.showValidationMessage('Subject is required.');
          return null;
        }

        return { toEmails: unique, subject, body };
      }
    }).then(async result => {
      if (!result.isConfirmed) return;

      const payload = result.value as any;
      const toEmails = payload && payload.toEmails ? (payload.toEmails as string[]) : [];
      const subject = payload && payload.subject ? String(payload.subject) : '';
      const body = payload && payload.body ? String(payload.body) : '';
      if (!toEmails || toEmails.length === 0) return;

      const contentEl = document.getElementById('annual-tax-receipt-content');
      const html = contentEl ? String(contentEl.innerHTML) : '';

      this.showLoadingMessage = true;

      this.reportService.sendAnnualTaxReceiptEmail({
        pharmacyId: this.pharmacyId,
        facilityId: this.facilityId,
        patientId: this.patientId,
        year,
        toEmails,
        subject,
        body,
        fileBaseName,
        html,
        performedByUserId: this.sharedService.getUserId()
      })
        .pipe(timeout(6000000))
        .subscribe(data => {
          this.showLoadingMessage = false;

          if (data && data.success === false) {
            this.loadErrorMessage("Oops! Something went wrong, please try again in a few minutes.");
            return;
          }

          Swal.fire({
            icon: 'success',
            title: 'Email queued',
            html: `
              <p>The annual tax receipt email has been queued for sending.</p>
              <p><strong>Recipients:</strong> ${toEmails.join(', ')}</p>
            `,
            confirmButtonText: 'OK'
          });
        }, error => {
          this.showLoadingMessage = false;

          if (error && error.status === 404) {
            Swal.fire({
              icon: 'info',
              title: 'Not implemented yet',
              text: 'Email sending API is not available yet. Please implement it on the server and try again.',
              confirmButtonText: 'OK'
            });
            return;
          }

          this.loadErrorMessage("Oops! Something went wrong, please try again in a few minutes.");
        });
    });
  }

  @ViewChild('RemoveConfirmModalClosebutton', { static: false }) closebutton;

  get canUpdateProfile(): boolean {
    return this.profileData.sageName != null && this.profileData.sageName.trim().length > 0;
  }

  /** Profile row exists in DB (can clear Sage fields and balance forward) */
  get canDeleteSageProfile(): boolean {
    const c = this.sageBalanceForward && this.sageBalanceForward.customer;
    const id = c ? c.id : null;
    return id != null && id > 0;
  }

  searchSageCustomer() {
    if (!this.sageSearchTerm || !this.sageSearchTerm.trim()) {
      return;
    }
    this.sageSearching = true;
    this.sageSearchResults = [];
    const request: ISageCustomerSearchRequestDto = {
      name: this.sageSearchTerm.trim(),
      accountNumber: '',
      page: 1,
      limit: 10
    };
    this.invoiceService.searchSageCustomer(request).subscribe(
      results => {
        this.sageSearchResults = results || [];
        this.sageSearching = false;
      },
      () => {
        this.sageSearching = false;
        this.setDefaultErrorHandler(null);
      }
    );
  }

  selectSageCustomer(customer: ISageCustomerDto) {
    if (!customer) {
      return;
    }
    this.profileData = {
      sageName: customer.name || '',
      sageCustomerId: customer.id != null ? customer.id : null,
      contact: customer.contact || '',
      email: customer.email || '',
      accountNumber: customer.accountNumber || '',
      paymentType: customer.paymentType || '',
      street1: customer.street1 || '',
      street2: customer.street2 || '',
      city: customer.city || '',
      province: customer.province || '',
      postalCode: customer.postalCode || '',
      country: customer.country || 'Canada',
      // Keep email send permission as stored in DB; do not change on search
      canSendEmail: this.sageBalanceForward.customer
        ? this.sageBalanceForward.customer.canSendEmail
        : false,
      phone1: this.phoneOrEmpty(customer.phone1),
      phone2: this.phoneOrEmpty(customer.phone2)
    };
    this.sageSearchResults = [];
    this.sageSearchTerm = '';
  }

  clearSageSearch() {
    this.sageSearchTerm = '';
    this.sageSearchResults = [];
  }

  private phoneOrEmpty(value: string): string {
    return value != null && value !== '' ? String(value) : '';
  }

  // Modal editing methods
  openEditModal() {
    this.clearSageSearch();
    const customer = this.sageBalanceForward && this.sageBalanceForward.customer
      ? this.sageBalanceForward.customer
      : null;
    if (!customer) {
      return;
    }
    this.profileData = {
      sageName: customer.sageName || '',
      sageCustomerId: customer.sageCustomerId != null ? customer.sageCustomerId : null,
      contact: customer.contact || '',
      email: customer.email || '',
      accountNumber: customer.accountNumber || '',
      paymentType: customer.paymentType || '',
      street1: customer.street1 || '',
      street2: customer.street2 || '',
      city: customer.city || '',
      province: customer.province || '',
      postalCode: customer.postalCode || '',
      country: customer.country || 'Canada',
      // if DB has value, use it; otherwise default to true
      canSendEmail: typeof customer.canSendEmail === 'boolean' ? customer.canSendEmail : true,
      phone1: this.phoneOrEmpty(customer.phone1),
      phone2: this.phoneOrEmpty(customer.phone2)
    };
  }

  onUpdateProfile() {
    if (!this.profileData.sageName || !this.profileData.sageName.trim()) {
      this.loadErrorMessage('Sage Name cannot be empty.');
      return;
    }

    // Validate email and postal code
    this.validateEmail();
    this.validatePostalCode();

    if (this.emailInvalid || this.postalCodeInvalid) {
      this.loadErrorMessage('Please fix validation errors before saving.');
      return;
    }

    // Get customer name safely
    const customerName = this.sageBalanceForward && this.sageBalanceForward.customer && this.sageBalanceForward.customer.name 
      ? this.sageBalanceForward.customer.name 
      : 'this customer';

    // Show confirmation dialog
    Swal.fire({
      title: 'Update Sage Profile?',
      html: `
        <p>You are about to update the Sage profile information for:</p>
        <p class="fw-bold">${customerName}</p>
        <p class="mt-3">This will update all contact and address information in the system.</p>
        <p class="text-muted small">Please review the information carefully before confirming.</p>
      `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#198754',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, Update Profile',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        this.saveProfile();
      }
    });
  }

  onDeleteSageProfile() {
    if (!this.canDeleteSageProfile) {
      return;
    }

    const customerName = this.sageBalanceForward && this.sageBalanceForward.customer && this.sageBalanceForward.customer.name
      ? this.sageBalanceForward.customer.name
      : 'this patient';

    Swal.fire({
      title: 'Delete Sage profile data?',
      html: `
        <p>This will permanently clear Sage name, customer link, contact, address, email, account, and payment type for:</p>
        <p class="fw-bold">${customerName}</p>
        <p class="text-danger mt-3 mb-0"><strong>All stored balance forward transactions for this patient will be removed.</strong></p>
        <p class="text-muted small mt-2 mb-0">This cannot be undone.</p>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, delete',
      cancelButtonText: 'Cancel',
      focusCancel: true
    }).then((result) => {
      if (!result.isConfirmed) {
        return;
      }

      this.showLoadingMessage = true;
      this.invoiceService.clearSageProfile(
        this.sageBalanceForward.customer.id,
        this.pharmacyId,
        this.facilityId,
        this.patientId
      ).subscribe(
        res => {
          this.showLoadingMessage = false;
          if (!res.success) {
            this.setDefaultErrorHandler(res.data);
            return;
          }

          this.profileData = {
            sageName: '',
            sageCustomerId: null,
            contact: '',
            email: '',
            accountNumber: '',
            paymentType: '',
            street1: '',
            street2: '',
            city: '',
            province: '',
            postalCode: '',
            country: 'Canada',
            canSendEmail: true,
            phone1: '',
            phone2: ''
          };

          const c = this.sageBalanceForward.customer;
          c.sageName = '';
          c.sageCustomerId = null;
          c.contact = '';
          c.email = '';
          c.accountNumber = '';
          c.paymentType = '';
          c.street1 = '';
          c.street2 = '';
          c.city = '';
          c.province = '';
          c.postalCode = '';
          c.country = '';
          c.phone1 = '';
          c.phone2 = '';
          this.sageBalanceForward.transactionList = [];
          this.sageBalanceForward.balanceForward = 0;
          this.pruneBalanceForwardFilters();

          if (this.closebutton && this.closebutton.nativeElement) {
            this.closebutton.nativeElement.click();
          }

          this.displaySuccessMessage = true;
          this.successMessage = res.data as string || 'Sage profile and balance data were cleared.';
          setTimeout(() => {
            this.displaySuccessMessage = false;
          }, 4000);

          this.loadData(false);
        },
        err => {
          this.showLoadingMessage = false;
          const errPayload = err && err.error && err.error.data !== undefined ? err.error.data : (err && err.data !== undefined ? err.data : null);
          this.setDefaultErrorHandler(errPayload);
        }
      );
    });
  }

  private saveProfile() {
    this.showLoadingMessage = true;

    this.invoiceService.updateSageProfile(
      this.sageBalanceForward.customer.id,
      this.pharmacyId,
      this.facilityId,
      this.patientId,
      this.profileData
    ).subscribe(
      res => {
        if (!res.success) {
          this.setDefaultErrorHandler(res.data);
          this.showLoadingMessage = false;
          return;
        }

        // Update the customer data
        const customer = this.sageBalanceForward && this.sageBalanceForward.customer
          ? this.sageBalanceForward.customer
          : null;
        if (!customer) {
          this.showLoadingMessage = false;
          return;
        }
        customer.sageName = this.profileData.sageName;
        customer.sageCustomerId = this.profileData.sageCustomerId;
        customer.contact = this.profileData.contact;
        customer.email = this.profileData.email;
        customer.accountNumber = this.profileData.accountNumber;
        customer.paymentType = this.profileData.paymentType;
        customer.street1 = this.profileData.street1;
        customer.street2 = this.profileData.street2;
        customer.city = this.profileData.city;
        customer.province = this.profileData.province;
        customer.postalCode = this.profileData.postalCode;
        customer.country = this.profileData.country;
        customer.canSendEmail = this.profileData.canSendEmail;
        customer.phone1 = this.phoneOrEmpty(this.profileData.phone1);
        customer.phone2 = this.phoneOrEmpty(this.profileData.phone2);
        
        this.showLoadingMessage = false;
        
        // Close the modal programmatically
        this.closebutton.nativeElement.click();
        
        // Show success message
        this.displaySuccessMessage = true;
        this.successMessage = 'Sage profile updated successfully.';
        setTimeout(() => {
          this.displaySuccessMessage = false;
        }, 3000);
        
        // Refresh balance forward from Sage
        this.onGetProfileFromSage();
      },
      error => {
        this.setDefaultErrorHandler(error.data);
        this.showLoadingMessage = false;
      }
    );
  }

  // Email validation
  validateEmail() {
    if (!this.profileData.email || this.profileData.email.trim() === '') {
      this.emailInvalid = false;
      return;
    }
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    this.emailInvalid = !emailRegex.test(this.profileData.email);
  }

  // Canadian postal code validation (A1A1A1 format - 6 characters, no space)
  validatePostalCode() {
    if (!this.profileData.postalCode || this.profileData.postalCode.trim() === '') {
      this.postalCodeInvalid = false;
      return;
    }
    
    // Remove any spaces and convert to uppercase
    const cleanedCode = this.profileData.postalCode.replace(/\s/g, '').toUpperCase();
    this.profileData.postalCode = cleanedCode;
    
    // Validate: Letter-Digit-Letter-Digit-Letter-Digit (6 characters)
    const postalCodeRegex = /^[A-Z]\d[A-Z]\d[A-Z]\d$/;
    this.postalCodeInvalid = !postalCodeRegex.test(cleanedCode);
  }

  get enablePrint(): boolean {
    if (this.invoiceList.length === 0) return false;

    let invoiceIds = this.invoiceList.filter(item =>
      item.invoiceReviewStatusId === 3
      && item.invoiceSageStatusId >= 3
      && item.total > 0).map(item => item.id);

    return Array.isArray(invoiceIds) && invoiceIds.length > 0;
  }

  constructor(private sharedService: SharedService,
    private invoiceService: InvoiceService,
    private paymentLinksService: PaymentLinksService,
    private patientService: PatientService,
    private residentService: ResidentService,
    private reportService: ReportService,
    private settingsService: SettingsService,
    private router: Router,
    private route: ActivatedRoute,
    private datepipe: DatePipe) { }
  

  ngOnInit() {
    const self = this;
    this.sharedService.reloadUserPrivileges();
    this.patientProfileImageUrl = this.patientProfileImageFallback;
    const selfBanner = this;
    setTimeout(function () {
      selfBanner.loadPatientProfileImageSettingsThenAvatar();
    }, 0);

    this.sageSearchSubscription = this.sageSearchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(term => {
      this.sageSearchTerm = term;
      if (!term) {
        this.sageSearchResults = [];
        return;
      }
      this.searchSageCustomer();
    });

    this.route.params.subscribe(params => {
      this.pharmacyId = +params['pharmacyId'];
      this.facilityId = +params['facilityId'];
      this.patientId = +params['patientId'];

      if (isNaN(this.pharmacyId)        
        || isNaN(this.facilityId)
        || isNaN(this.patientId))
      {
        this.router.navigate(['invoice/profile']);
        return;
      }

      this.annualTaxReceiptActionLogs = [];
      this.annualTaxReceiptActionLogError = '';
      this.loadAnnualTaxReceiptActionLogs();
      this.paymentSetupLinkActionLogs = [];
      this.paymentSetupLinkActionLogError = '';
      this.paymentMethodSetupCodes = [];
      this.paymentMethodSetupCodesError = '';
      this.paymentLinkShareLogs = [];
      this.paymentActionLogs = [];
      this.patientPaymentMethods = null;
      this.patientPaymentMethodsError = '';
      this.loadPatientPaymentMethods();
      this.loadPaymentSetupCodesWithActivity();
      this.loadData(false);
      this.loadPatientClinicalBanner();
      this.updatePatientProfileImageUrl();
      this.restoreProfileTabFromQuery();
    });

    this.route.queryParams.subscribe(function (qp) {
      const tabId = self.getProfileTabIdFromQuery(qp['tab']);
      if (tabId) {
        self.activeProfileTab = tabId;
        setTimeout(function () {
          self.updateAnnualScrollButtonVisibility();
        }, 0);
      }
    });

    this.updateAnnualScrollButtonVisibility();
  }

  @HostListener('window:scroll')
  onWindowScroll() {
    this.updateAnnualScrollButtonVisibility();
  }

  selectProfileTab(tabId: string) {
    if (!tabId) {
      return;
    }
    this.activeProfileTab = tabId;
    this.syncProfileTabQueryParam(tabId);
    if (tabId === 'payments') {
      this.loadPatientPaymentMethods();
      this.loadPaymentSetupCodesWithActivity();
    }
    setTimeout(() => this.updateAnnualScrollButtonVisibility(), 0);
  }

  /** Restore Finance Account list tab when returning via breadcrumb. */
  get financeAccountQueryParams(): { [key: string]: string } {
    const listTab = this.route.snapshot.queryParamMap.get('listTab');
    if (listTab === 'payments' || this.activeProfileTab === 'payments') {
      return { tab: 'payments' };
    }
    return {};
  }

  private restoreProfileTabFromQuery() {
    const tabId = this.getProfileTabIdFromQuery(this.route.snapshot.queryParamMap.get('tab'));
    if (tabId) {
      this.activeProfileTab = tabId;
    }
  }

  private syncProfileTabQueryParam(tabId: string) {
    const tabQuery = this.getProfileTabQueryValue(tabId);
    const queryParams: any = {};
    if (tabQuery && tabQuery !== 'profile') {
      queryParams.tab = tabQuery;
    } else {
      queryParams.tab = null;
    }
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryParams,
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  private getProfileTabQueryValue(tabId: string): string | null {
    const map: { [key: string]: string } = {
      'sage-profile': 'profile',
      'patient-balance-forward': 'balance',
      'patient-invoice-list': 'invoices',
      'year-tax': 'annual',
      'payments': 'payments'
    };
    return map[tabId] || null;
  }

  private getProfileTabIdFromQuery(queryValue: string): string | null {
    if (!queryValue) {
      return null;
    }
    const map: { [key: string]: string } = {
      profile: 'sage-profile',
      balance: 'patient-balance-forward',
      invoices: 'patient-invoice-list',
      annual: 'year-tax',
      payments: 'payments'
    };
    return map[queryValue] || null;
  }

  openPaymentVerifyPopup() {
    if (this.paymentVerifyLinkLoading) {
      return;
    }
    if (isNaN(this.pharmacyId) || isNaN(this.facilityId) || isNaN(this.patientId)) {
      this.errorMessage = 'Invalid patient scope for payment method setup.';
      this.showErrorMessage = true;
      return;
    }

    const self = this;
    Swal.fire({
      icon: 'question',
      title: 'Set Up Payment Method?',
      html: '<div style="text-align:left;">'
        + '<p class="mb-2">Clicking <strong>Continue</strong> will create a new payment method setup link.</p>'
        + '<p class="mb-0">Previous unused links remain valid until they expire or are used.</p>'
        + '</div>',
      showCancelButton: true,
      cancelButtonText: 'Cancel',
      confirmButtonText: 'Continue'
    }).then(function (swalResult) {
      if (!swalResult || !swalResult.isConfirmed) {
        return;
      }
      self.createAndOpenPaymentMethodSetupLink();
    });
  }

  private createAndOpenPaymentMethodSetupLink() {
    if (this.paymentVerifyLinkLoading) {
      return;
    }

    this.paymentVerifyLinkLoading = true;
    this.clearErrorMessage();
    const self = this;
    this.invoiceService.getPaymentVerifyLinkEmailOptions(this.pharmacyId, this.facilityId, this.patientId).subscribe(
      function (optionsResult) {
        if (!optionsResult || !optionsResult.success || !optionsResult.data || !optionsResult.data.accessKey) {
          self.paymentVerifyLinkLoading = false;
          self.errorMessage = 'Patient was not found in the Portal.';
          self.showErrorMessage = true;
          return;
        }

        self.paymentLinksService.generate(optionsResult.data.accessKey).subscribe(
          function (generateResult) {
            self.paymentVerifyLinkLoading = false;
            if (!generateResult || !generateResult.paymentMethodSetupLink) {
              self.errorMessage = 'Failed to create payment method setup link.';
              self.showErrorMessage = true;
              self.logPaymentMethodSetupLinkCreate(undefined, undefined, false, 'Failed to create payment method setup link.');
              return;
            }
            self.logPaymentMethodSetupLinkCreate(
              generateResult.code,
              generateResult.paymentMethodSetupLink,
              true,
              undefined);
            self.openCenteredPopup(generateResult.paymentMethodSetupLink, 'solvoPaymentMethodSetup', 520, 780);
            self.loadPaymentSetupCodesWithActivity();
          },
          function (err) {
            self.paymentVerifyLinkLoading = false;
            const msg = err && err.error && err.error.message
              ? String(err.error.message)
              : 'Failed to create payment method setup link.';
            self.errorMessage = msg;
            self.showErrorMessage = true;
            self.logPaymentMethodSetupLinkCreate(undefined, undefined, false, msg);
          }
        );
      },
      function (err) {
        self.paymentVerifyLinkLoading = false;
        const msg = err && err.error && err.error.data
          ? String(err.error.data)
          : 'Failed to load patient access key.';
        self.errorMessage = msg;
        self.showErrorMessage = true;
      }
    );
  }

  openPaymentVerifyEmailModal() {
    if (this.paymentVerifyLinkEmailLoading) {
      return;
    }
    if (isNaN(this.pharmacyId) || isNaN(this.facilityId) || isNaN(this.patientId)) {
      this.errorMessage = 'Invalid patient scope for payment method setup email.';
      this.showErrorMessage = true;
      return;
    }

    this.paymentVerifyLinkEmailLoading = true;
    this.clearErrorMessage();
    const self = this;
    this.invoiceService.getPaymentVerifyLinkEmailOptions(this.pharmacyId, this.facilityId, this.patientId).subscribe(
      function (result) {
        self.paymentVerifyLinkEmailLoading = false;
        if (!result || !result.success || !result.data) {
          self.errorMessage = (result && result.data) ? String(result.data) : 'Failed to load email options.';
          self.showErrorMessage = true;
          return;
        }

        const primaryEmail = result.data.patientEmail || '';
        const allowPrimary = !!result.data.allowPatientEmail;
        const isProduction = !!result.data.isProduction;
        const primaryLabel = result.data.primaryRecipientLabel || 'Patient email';
        const primaryChecked = allowPrimary ? 'checked' : '';
        const customChecked = allowPrimary ? '' : 'checked';
        const primaryDisabled = allowPrimary ? '' : 'disabled';
        const primaryEmailRow = primaryEmail
          ? self.escapeHtml(primaryEmail)
          : '<span class="text-muted">(not available)</span>';
        const envNote = !isProduction
          ? '<div class="text-danger small mb-2">This is not production. Patient email can only be selected in the production environment. Please use Custom email.</div>'
          : '';

        const subjectDefault = result.data.emailSubject || 'Please set up your payment method — Advantage Care Pharmacy';
        // Preview only: keep button look without exposing a real URL.
        const bodyPreview = (result.data.emailBody || '')
          .replace(/\{\{PAYMENT_REGISTER_URL\}\}/g, '#');

        Swal.fire({
          icon: 'info',
          iconHtml: self.swalEnvelopeIconHtml(),
          title: 'Email Payment Method Setup Link?',
          width: 800,
          html: `
            <div style="text-align:left;">
              <p class="text-muted mb-3">Send a secure link to set up a payment method (card or ACH). Emails are sent from solvoit.notification@gmail.com.</p>
              ${envNote}
              <div class="mb-2 fw-bold">Select recipient</div>

              <div class="mb-2" style="padding-left:20px;">
              <style>
                .payment-recipient-table td { vertical-align: middle; }
                .payment-recipient-table .form-check-input {
                  margin: 0 !important;
                  float: none !important;
                  position: static !important;
                  width: 16px;
                  height: 16px;
                }
                .payment-recipient-cell {
                  display: flex;
                  align-items: center;
                  min-height: 32px;
                }
              </style>
              <table class="payment-recipient-table" style="width:100%;table-layout:fixed;border-collapse:collapse;">
                <colgroup>
                  <col style="width:32px;">
                  <col style="width:140px;">
                  <col>
                </colgroup>
                <tr>
                  <td style="padding:6px 8px 6px 0;">
                    <div class="payment-recipient-cell">
                      <input class="form-check-input" type="radio" name="payment-email-mode" id="payment-email-primary"
                             value="patient" ${primaryChecked} ${primaryDisabled}>
                    </div>
                  </td>
                  <td style="padding:6px 12px 6px 0;white-space:nowrap;">
                    <div class="payment-recipient-cell">
                      <label class="mb-0" for="payment-email-primary">${self.escapeHtml(primaryLabel)}:</label>
                    </div>
                  </td>
                  <td style="padding:6px 0;">
                    <div class="payment-recipient-cell">
                      <span class="text-muted">${primaryEmailRow}</span>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td style="padding:6px 8px 6px 0;">
                    <div class="payment-recipient-cell">
                      <input class="form-check-input" type="radio" name="payment-email-mode" id="payment-email-custom"
                             value="custom" ${customChecked}>
                    </div>
                  </td>
                  <td style="padding:6px 12px 6px 0;white-space:nowrap;">
                    <div class="payment-recipient-cell">
                      <label class="mb-0" for="payment-email-custom">Custom email</label>
                    </div>
                  </td>
                  <td style="padding:6px 0;">
                    <input id="payment-email-custom-input" class="form-control form-control-sm" type="email"
                           placeholder="Enter email address" style="max-width:280px;"
                           autocomplete="email">
                    <div id="payment-email-custom-error" class="small text-danger mt-1" style="display:none;">
                      Please enter a valid email address.
                    </div>
                  </td>
                </tr>
              </table>
              </div>

              <hr class="my-3" />
              <div class="mb-2 fw-bold">Email content (preview)</div>
              <div style="border:1px solid #dee2e6;border-radius:6px;padding:12px 14px;background:#f8f9fa;">
                <div class="d-flex align-items-baseline mb-2">
                  <div style="font-size:12px;color:#6c757d;min-width:64px;">Subject</div>
                  <div style="font-weight:600;">${self.escapeHtml(subjectDefault)}</div>
                </div>
                <div class="d-flex">
                  <div style="font-size:12px;color:#6c757d;min-width:64px;padding-top:12px;">Body</div>
                  <div style="background:#fff;border:1px solid #e9ecef;border-radius:4px;padding:12px;max-height:280px;overflow:auto;flex:1;">
                    ${bodyPreview}
                  </div>
                </div>
              </div>
            </div>
          `,
          showCancelButton: true,
          cancelButtonText: 'Cancel',
          confirmButtonText: 'Send Email',
          onOpen: function () {
            self.sizeSwalCustomIcon();
            const primaryRadio = document.getElementById('payment-email-primary') as HTMLInputElement;
            const customRadio = document.getElementById('payment-email-custom') as HTMLInputElement;
            const customInput = document.getElementById('payment-email-custom-input') as HTMLInputElement;
            const customError = document.getElementById('payment-email-custom-error') as HTMLElement;
            if (!customRadio || !customInput) {
              return;
            }
            const selectCustom = function () {
              customRadio.checked = true;
            };
            const syncCustomEmailInput = function () {
              const customVal = customInput.value ? String(customInput.value).trim() : '';
              if (customVal) {
                customRadio.checked = true;
              } else if (primaryRadio && !primaryRadio.disabled) {
                primaryRadio.checked = true;
              } else {
                customRadio.checked = true;
              }
              const isInvalid = customVal.length > 0 && !self.isValidEmail(customVal);
              if (isInvalid) {
                customInput.classList.add('is-invalid');
              } else {
                customInput.classList.remove('is-invalid');
              }
              if (customError) {
                customError.style.display = isInvalid ? 'block' : 'none';
              }
            };
            customInput.addEventListener('focus', selectCustom);
            customInput.addEventListener('click', selectCustom);
            customInput.addEventListener('input', syncCustomEmailInput);
            customInput.addEventListener('blur', syncCustomEmailInput);
          },
          preConfirm: function () {
            const customRadio = document.getElementById('payment-email-custom') as any;
            const customInput = document.getElementById('payment-email-custom-input') as any;
            const useCustom = customRadio && customRadio.checked;

            if (useCustom) {
              const customVal = customInput && customInput.value ? String(customInput.value).trim() : '';
              if (!customVal) {
                Swal.showValidationMessage('Please enter a custom email address.');
                return null;
              }
              if (!self.isValidEmail(customVal)) {
                Swal.showValidationMessage('Custom email address is invalid.');
                return null;
              }
              return {
                recipientMode: 'custom',
                customEmail: customVal
              };
            }

            if (!allowPrimary || !primaryEmail || !self.isValidEmail(primaryEmail)) {
              Swal.showValidationMessage(primaryLabel + ' is missing or invalid.');
              return null;
            }

            return {
              recipientMode: 'patient',
              customEmail: null
            };
          }
        }).then(function (swalResult) {
          if (!swalResult || !swalResult.isConfirmed || !swalResult.value) {
            return;
          }

          const payload = swalResult.value as any;
          self.paymentVerifyLinkEmailLoading = true;
          Swal.fire({
            title: 'Sending...',
            allowOutsideClick: false,
            allowEscapeKey: false,
            onOpen: function () {
              Swal.showLoading();
            }
          });

          self.invoiceService.sendPaymentVerifyLinkEmail({
            pharmacyId: self.pharmacyId,
            facilityId: self.facilityId,
            patientId: self.patientId,
            recipientMode: payload.recipientMode,
            customEmail: payload.customEmail,
            performedByUserId: self.sharedService.getUserId()
          }).subscribe(
            function (sendResult) {
              self.paymentVerifyLinkEmailLoading = false;
              if (!sendResult || !sendResult.success) {
                const msg = (sendResult && sendResult.data)
                  ? String(sendResult.data)
                  : 'Failed to send payment method setup email.';
                Swal.fire({
                  icon: 'error',
                  title: 'Send failed',
                  text: msg,
                  confirmButtonText: 'OK'
                });
                return;
              }

              self.loadPaymentSetupCodesWithActivity();
              const sentTo = payload.recipientMode === 'custom'
                ? payload.customEmail
                : primaryEmail;
              Swal.fire({
                icon: 'success',
                title: 'Email sent',
                html: '<p>The payment method setup link email was sent.</p><p><strong>Recipient:</strong> '
                  + self.escapeHtml(sentTo) + '</p>',
                confirmButtonText: 'OK'
              });
            },
            function (err) {
              self.paymentVerifyLinkEmailLoading = false;
              const msg = err && err.error && err.error.data
                ? String(err.error.data)
                : 'Failed to send payment method setup email.';
              Swal.fire({
                icon: 'error',
                title: 'Send failed',
                text: msg,
                confirmButtonText: 'OK'
              });
            }
          );
        });
      },
      function (err) {
        self.paymentVerifyLinkEmailLoading = false;
        const msg = err && err.error && err.error.data
          ? String(err.error.data)
          : 'Failed to load email options.';
        self.errorMessage = msg;
        self.showErrorMessage = true;
      }
    );
  }

  openPaymentVerifySmsModal() {
    if (this.paymentVerifyLinkSmsLoading) {
      return;
    }
    if (isNaN(this.pharmacyId) || isNaN(this.facilityId) || isNaN(this.patientId)) {
      this.errorMessage = 'Invalid patient scope for payment method setup SMS.';
      this.showErrorMessage = true;
      return;
    }

    this.paymentVerifyLinkSmsLoading = true;
    this.clearErrorMessage();
    const self = this;
    this.invoiceService.getPaymentVerifyLinkSmsOptions(this.pharmacyId, this.facilityId, this.patientId).subscribe(
      function (result) {
        self.paymentVerifyLinkSmsLoading = false;
        if (!result || !result.success || !result.data) {
          self.errorMessage = (result && result.data) ? String(result.data) : 'Failed to load SMS options.';
          self.showErrorMessage = true;
          return;
        }

        const phone1 = result.data.phone1 || '';
        const phone2 = result.data.phone2 || '';
        const allowPhone1 = !!result.data.allowPhone1;
        const allowPhone2 = !!result.data.allowPhone2;
        const isProduction = !!result.data.isProduction;
        const maxLength = result.data.smsMaxLength > 0 ? result.data.smsMaxLength : 160;
        const previewBody = result.data.smsBody || '';
        const phone1Checked = allowPhone1 ? 'checked' : '';
        const phone2Checked = !allowPhone1 && allowPhone2 ? 'checked' : '';
        const customChecked = !allowPhone1 && !allowPhone2 ? 'checked' : '';
        const phone1Disabled = allowPhone1 ? '' : 'disabled';
        const phone2Disabled = allowPhone2 ? '' : 'disabled';
        const phone1Row = phone1
          ? self.escapeHtml(phone1)
          : '<span class="text-muted">(not available)</span>';
        const phone2Row = phone2
          ? self.escapeHtml(phone2)
          : '<span class="text-muted">(not available)</span>';
        const envNote = !isProduction
          ? '<div class="text-danger small mb-2">This is not production. Phone 1 and Phone 2 can only be selected in the production environment. Please use Custom phone.</div>'
          : '';

        Swal.fire({
          icon: 'success',
          iconHtml: self.swalChatIconHtml(),
          title: 'SMS Payment Method Setup Link?',
          width: 640,
          html: `
            <div style="text-align:left;">
              <p class="text-muted mb-3">Send a short setup link by SMS. Keep the message within one SMS (${maxLength} characters). The short URL slot is reserved at 35 characters and is filled when the message is sent.</p>
              ${envNote}
              <div class="mb-2 fw-bold">Select recipient</div>

              <div class="mb-2" style="padding-left:20px;">
              <style>
                .payment-recipient-table td { vertical-align: middle; }
                .payment-recipient-table .form-check-input {
                  margin: 0 !important;
                  float: none !important;
                  position: static !important;
                  width: 16px;
                  height: 16px;
                }
                .payment-recipient-cell {
                  display: flex;
                  align-items: center;
                  min-height: 32px;
                }
              </style>
              <table class="payment-recipient-table" style="width:100%;table-layout:fixed;border-collapse:collapse;">
                <colgroup>
                  <col style="width:32px;">
                  <col style="width:140px;">
                  <col>
                </colgroup>
                <tr>
                  <td style="padding:6px 8px 6px 0;">
                    <div class="payment-recipient-cell">
                      <input class="form-check-input" type="radio" name="payment-sms-mode" id="payment-sms-phone1"
                             value="phone1" ${phone1Checked} ${phone1Disabled}>
                    </div>
                  </td>
                  <td style="padding:6px 12px 6px 0;white-space:nowrap;">
                    <div class="payment-recipient-cell">
                      <label class="mb-0" for="payment-sms-phone1">Phone 1:</label>
                    </div>
                  </td>
                  <td style="padding:6px 0;">
                    <div class="payment-recipient-cell">
                      <span class="text-muted">${phone1Row}</span>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td style="padding:6px 8px 6px 0;">
                    <div class="payment-recipient-cell">
                      <input class="form-check-input" type="radio" name="payment-sms-mode" id="payment-sms-phone2"
                             value="phone2" ${phone2Checked} ${phone2Disabled}>
                    </div>
                  </td>
                  <td style="padding:6px 12px 6px 0;white-space:nowrap;">
                    <div class="payment-recipient-cell">
                      <label class="mb-0" for="payment-sms-phone2">Phone 2:</label>
                    </div>
                  </td>
                  <td style="padding:6px 0;">
                    <div class="payment-recipient-cell">
                      <span class="text-muted">${phone2Row}</span>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td style="padding:6px 8px 6px 0;">
                    <div class="payment-recipient-cell">
                      <input class="form-check-input" type="radio" name="payment-sms-mode" id="payment-sms-custom"
                             value="custom" ${customChecked}>
                    </div>
                  </td>
                  <td style="padding:6px 12px 6px 0;white-space:nowrap;">
                    <div class="payment-recipient-cell">
                      <label class="mb-0" for="payment-sms-custom">Custom phone</label>
                    </div>
                  </td>
                  <td style="padding:6px 0;">
                    <div class="payment-recipient-cell">
                      <div class="input-group input-group-sm" style="max-width:220px;">
                        <span class="input-group-text">+1</span>
                        <input id="payment-sms-custom-input" class="form-control" type="tel"
                               placeholder="9055551234" maxlength="20" autocomplete="tel-national">
                      </div>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td></td>
                  <td></td>
                  <td style="padding:2px 0 6px 0;vertical-align:top;">
                    <div id="payment-sms-custom-error" class="small text-danger mb-1" style="display:none;">
                      Enter a valid phone number, for example (905) 555-1234, 905-555-1234, or 9055551234.
                    </div>
                    <div class="small">
                      <strong>Canadian (+1) numbers only.</strong>
                      You can enter (905) 555-1234, 905-555-1234, or 9055551234.
                    </div>
                  </td>
                </tr>
              </table>
              </div>

              <hr class="my-3" />
              <div class="d-flex justify-content-between align-items-center mb-2">
                <div class="fw-bold">SMS message</div>
                <div id="payment-sms-count" class="small text-muted"></div>
              </div>
              <div style="border:1px solid #dee2e6;border-radius:6px;padding:12px 14px;background:#f8f9fa;">
                <textarea id="payment-sms-body" class="form-control" rows="5"
                          style="font-family: Consolas, Monaco, monospace; font-size: 14px; background:#fff; border:1px solid #e9ecef;">${self.escapeHtml(previewBody)}</textarea>
              </div>
              <div class="small text-muted mt-1">Keep the PAYMENT_REGISTER_URL placeholder in the message. It reserves 35 characters for the short URL. One SMS is ${maxLength} characters.</div>
            </div>
          `,
          showCancelButton: true,
          cancelButtonText: 'Cancel',
          confirmButtonText: 'Send SMS',
          onOpen: function () {
            self.sizeSwalCustomIcon();
            const phone1Radio = document.getElementById('payment-sms-phone1') as HTMLInputElement;
            const phone2Radio = document.getElementById('payment-sms-phone2') as HTMLInputElement;
            const customRadio = document.getElementById('payment-sms-custom') as HTMLInputElement;
            const customInput = document.getElementById('payment-sms-custom-input') as HTMLInputElement;
            const bodyInput = document.getElementById('payment-sms-body') as HTMLTextAreaElement;
            const countEl = document.getElementById('payment-sms-count');

            const updateCount = function () {
              if (!bodyInput || !countEl) {
                return;
              }
              const len = bodyInput.value ? bodyInput.value.length : 0;
              countEl.textContent = len + ' / ' + maxLength;
              countEl.className = len > maxLength ? 'small text-danger' : 'small text-muted';
            };
            updateCount();

            if (bodyInput) {
              bodyInput.addEventListener('input', updateCount);
            }

            if (!customRadio || !customInput) {
              return;
            }
            const customError = document.getElementById('payment-sms-custom-error') as HTMLElement;
            const selectCustom = function () {
              customRadio.checked = true;
            };
            const syncCustomPhoneInput = function (showError) {
              var cleaned = self.sanitizeNationalPhoneInput(customInput.value);
              if (customInput.value !== cleaned) {
                customInput.value = cleaned;
              }
              var digits = cleaned.replace(/\D/g, '');
              if (digits) {
                customRadio.checked = true;
              } else if (phone1Radio && !phone1Radio.disabled) {
                phone1Radio.checked = true;
              } else if (phone2Radio && !phone2Radio.disabled) {
                phone2Radio.checked = true;
              } else {
                customRadio.checked = true;
              }
              const isInvalid = !!showError && digits.length > 0
                && !self.normalizeCustomNationalPhoneToE164(cleaned);
              if (isInvalid) {
                customInput.classList.add('is-invalid');
              } else {
                customInput.classList.remove('is-invalid');
              }
              if (customError) {
                customError.style.display = isInvalid ? 'block' : 'none';
              }
            };
            customInput.addEventListener('focus', selectCustom);
            customInput.addEventListener('click', selectCustom);
            customInput.addEventListener('input', function () {
              syncCustomPhoneInput(false);
            });
            customInput.addEventListener('blur', function () {
              syncCustomPhoneInput(true);
            });
          },
          preConfirm: function () {
            const phone1Radio = document.getElementById('payment-sms-phone1') as any;
            const phone2Radio = document.getElementById('payment-sms-phone2') as any;
            const customRadio = document.getElementById('payment-sms-custom') as any;
            const customInput = document.getElementById('payment-sms-custom-input') as any;
            const bodyInput = document.getElementById('payment-sms-body') as any;
            const useCustom = customRadio && customRadio.checked;
            const smsBody = bodyInput && bodyInput.value ? String(bodyInput.value) : '';

            if (!smsBody || !smsBody.trim()) {
              Swal.showValidationMessage('Please enter an SMS message.');
              return null;
            }
            if (smsBody.length > maxLength) {
              Swal.showValidationMessage('SMS message must be ' + maxLength + ' characters or fewer.');
              return null;
            }
            if (smsBody.indexOf('{{PAYMENT_REGISTER_URL}}') < 0
              && smsBody.toLowerCase().indexOf('http://') < 0
              && smsBody.toLowerCase().indexOf('https://') < 0) {
              Swal.showValidationMessage('SMS message must include the payment setup link.');
              return null;
            }

            if (useCustom) {
              const customVal = customInput && customInput.value ? String(customInput.value).trim() : '';
              if (!customVal) {
                Swal.showValidationMessage('Please enter a custom phone number.');
                return null;
              }
              const customE164 = self.normalizeCustomNationalPhoneToE164(customVal);
              if (!customE164) {
                Swal.showValidationMessage('Enter a valid phone number, for example (905) 555-1234, 905-555-1234, or 9055551234.');
                return null;
              }
              return {
                recipientMode: 'custom',
                customPhone: customE164,
                smsBody: smsBody
              };
            }

            if (phone1Radio && phone1Radio.checked) {
              if (!allowPhone1 || !self.isValidPhone(phone1)) {
                Swal.showValidationMessage('Phone 1 is missing or invalid.');
                return null;
              }
              return {
                recipientMode: 'phone1',
                customPhone: null,
                smsBody: smsBody
              };
            }

            if (phone2Radio && phone2Radio.checked) {
              if (!allowPhone2 || !self.isValidPhone(phone2)) {
                Swal.showValidationMessage('Phone 2 is missing or invalid.');
                return null;
              }
              return {
                recipientMode: 'phone2',
                customPhone: null,
                smsBody: smsBody
              };
            }

            Swal.showValidationMessage('Please select a recipient phone number.');
            return null;
          }
        }).then(function (swalResult) {
          if (!swalResult || !swalResult.isConfirmed || !swalResult.value) {
            return;
          }

          const payload = swalResult.value as any;
          self.paymentVerifyLinkSmsLoading = true;
          Swal.fire({
            title: 'Sending...',
            allowOutsideClick: false,
            allowEscapeKey: false,
            onOpen: function () {
              Swal.showLoading();
            }
          });

          self.invoiceService.sendPaymentVerifyLinkSms({
            pharmacyId: self.pharmacyId,
            facilityId: self.facilityId,
            patientId: self.patientId,
            recipientMode: payload.recipientMode,
            customPhone: payload.customPhone,
            smsBody: payload.smsBody,
            performedByUserId: self.sharedService.getUserId()
          }).subscribe(
            function (sendResult) {
              self.paymentVerifyLinkSmsLoading = false;
              if (!sendResult || !sendResult.success) {
                const msg = (sendResult && sendResult.data)
                  ? String(sendResult.data)
                  : 'Failed to send payment method setup SMS.';
                Swal.fire({
                  icon: 'error',
                  title: 'Send failed',
                  text: msg,
                  confirmButtonText: 'OK'
                });
                return;
              }

              self.loadPaymentSetupCodesWithActivity();
              var sentTo = payload.customPhone;
              if (payload.recipientMode === 'phone1') {
                sentTo = phone1;
              } else if (payload.recipientMode === 'phone2') {
                sentTo = phone2;
              }
              Swal.fire({
                icon: 'success',
                title: 'SMS sent',
                html: '<p>The payment method setup link SMS was sent.</p><p><strong>Recipient:</strong> '
                  + self.escapeHtml(sentTo) + '</p>',
                confirmButtonText: 'OK'
              });
            },
            function (err) {
              self.paymentVerifyLinkSmsLoading = false;
              const msg = err && err.error && err.error.data
                ? String(err.error.data)
                : 'Failed to send payment method setup SMS.';
              Swal.fire({
                icon: 'error',
                title: 'Send failed',
                text: msg,
                confirmButtonText: 'OK'
              });
            }
          );
        });
      },
      function (err) {
        self.paymentVerifyLinkSmsLoading = false;
        const msg = err && err.error && err.error.data
          ? String(err.error.data)
          : 'Failed to load SMS options.';
        self.errorMessage = msg;
        self.showErrorMessage = true;
      }
    );
  }

  private swalEnvelopeIconHtml(): string {
    return '<svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true">'
      + '<path d="M.05 3.555A2 2 0 0 1 2 2h12a2 2 0 0 1 1.95 1.555L8 8.414.05 3.555zM0 4.697v7.104l5.803-3.558L0 4.697zM6.761 8.83l-6.57 4.027A2 2 0 0 0 2 14h12a2 2 0 0 0 1.808-1.144l-6.57-4.027L8 9.586l-1.239-.757zm3.436-.586L16 11.801V4.697l-5.803 3.546z"/>'
      + '</svg>';
  }

  private swalChatIconHtml(): string {
    return '<svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true">'
      + '<path d="M16 8c0 3.866-3.582 7-8 7a9.06 9.06 0 0 1-2.347-.306c-.584.296-1.925.864-4.181 1.234-.2.032-.293-.312-.147-.517.254-.354.451-.723.587-1.126.248-.73.06-1.551-.546-2.057C.805 10.523 0 9.318 0 8c0-3.866 3.582-7 8-7s8 3.134 8 7zM5 8a1 1 0 1 0-2 0 1 1 0 0 0 2 0zm4 0a1 1 0 1 0-2 0 1 1 0 0 0 2 0zm3 1a1 1 0 1 0 0-2 1 1 0 0 0 0 2z"/>'
      + '</svg>';
  }

  private sizeSwalCustomIcon(): void {
    var content = document.querySelector('.swal2-icon-content') as HTMLElement;
    if (!content) {
      return;
    }
    content.style.fontSize = '1em';
    content.style.display = 'flex';
    content.style.alignItems = 'center';
    content.style.justifyContent = 'center';
  }

  private escapeHtml(value: string): string {
    if (!value) {
      return '';
    }
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  private openCenteredPopup(url: string, name: string, width: number, height: number) {
    const dualScreenLeft = window.screenLeft !== undefined ? window.screenLeft : (window as any).screenX;
    const dualScreenTop = window.screenTop !== undefined ? window.screenTop : (window as any).screenY;
    const screenWidth = window.innerWidth
      ? window.innerWidth
      : (document.documentElement.clientWidth ? document.documentElement.clientWidth : screen.width);
    const screenHeight = window.innerHeight
      ? window.innerHeight
      : (document.documentElement.clientHeight ? document.documentElement.clientHeight : screen.height);
    const left = Math.max(0, ((screenWidth - width) / 2) + dualScreenLeft);
    const top = Math.max(0, ((screenHeight - height) / 2) + dualScreenTop);
    const features = 'scrollbars=yes,resizable=yes,width=' + width
      + ',height=' + height
      + ',top=' + Math.round(top)
      + ',left=' + Math.round(left);
    const popup = window.open(url, name, features);
    if (popup && typeof popup.focus === 'function') {
      popup.focus();
    }
  }

  scrollAnnualInvoicesToTop() {
    const target = document.getElementById('annual-invoices-top');
    if (target && typeof target.scrollIntoView === 'function') {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  scrollAnnualInvoicesToBottom() {
    const doc = document.documentElement;
    const scrollTopMax = doc.scrollHeight - doc.clientHeight;
    window.scrollTo({ top: scrollTopMax, behavior: 'smooth' });
  }

  toggleAnnualTaxReceiptActionLog() {
    this.showAnnualTaxReceiptActionLogPanel = !this.showAnnualTaxReceiptActionLogPanel;
    if (this.showAnnualTaxReceiptActionLogPanel) {
      this.loadAnnualTaxReceiptActionLogs();
    } else {
      this.resetAnnualTaxReceiptActionLogPanel(false);
    }
  }

  /** Close/clear log UI; pass keepClosed false when panel is already hidden after route change. */
  private resetAnnualTaxReceiptActionLogPanel(closePanel: boolean = true) {
    if (closePanel) {
      this.showAnnualTaxReceiptActionLogPanel = false;
    }
    this.annualTaxReceiptActionLogs = [];
    this.annualTaxReceiptActionLogError = '';
    this.annualTaxReceiptActionLogLoading = false;
  }

  loadAnnualTaxReceiptActionLogs() {
    if (this.pharmacyId == null || isNaN(this.pharmacyId)
      || this.facilityId == null || isNaN(this.facilityId)
      || this.patientId == null || isNaN(this.patientId)) {
      return;
    }
    this.annualTaxReceiptActionLogLoading = true;
    this.annualTaxReceiptActionLogError = '';
    this.invoiceService.searchUserActionLogs({
      pharmacyId: this.pharmacyId,
      facilityId: this.facilityId,
      patientId: this.patientId,
      actionCodes: [
        'INVOICE_ANNUAL_TAX_RECEIPT_PDF_DOWNLOAD',
        'INVOICE_ANNUAL_TAX_RECEIPT_EMAIL_SEND'
      ],
      maxRows: 200
    }).subscribe(
      (rows) => {
        this.annualTaxReceiptActionLogs = rows && Array.isArray(rows) ? rows : [];
        this.annualTaxReceiptActionLogLoading = false;
      },
      () => {
        this.annualTaxReceiptActionLogLoading = false;
        this.annualTaxReceiptActionLogError = 'Could not load activity log.';
        this.annualTaxReceiptActionLogs = [];
      }
    );
  }

  closeAnnualTaxReceiptActionLogPanel() {
    this.resetAnnualTaxReceiptActionLogPanel(true);
  }

  cancelPaymentMethodSetupCode(row: any) {
    if (!row || !row.code || !row.canCancel || this.paymentMethodSetupCodeCancelingCode) {
      return;
    }
    const self = this;
    const code = String(row.code);
    Swal.fire({
      icon: 'warning',
      title: 'Cancel setup link?',
      html: '<div style="text-align:left;">'
        + '<p class="mb-2">This will immediately invalidate the setup link for code:</p>'
        + '<p class="mb-0"><code>' + self.escapeHtml(code) + '</code></p>'
        + '</div>',
      showCancelButton: true,
      cancelButtonText: 'Keep link',
      confirmButtonText: 'Cancel link',
      confirmButtonColor: '#dc3545'
    }).then(function (swalResult) {
      if (!swalResult || !swalResult.isConfirmed) {
        return;
      }
      self.paymentMethodSetupCodeCancelingCode = code;
      self.invoiceService.cancelPaymentMethodSetupCode({
        pharmacyId: self.pharmacyId,
        facilityId: self.facilityId,
        patientId: self.patientId,
        code: code
      }).subscribe(
        function (result) {
          self.paymentMethodSetupCodeCancelingCode = '';
          if (!result || !result.success) {
            const msg = (result && result.data)
              ? String(result.data)
              : 'Failed to cancel setup link.';
            Swal.fire({ icon: 'error', title: 'Cancel failed', text: msg, confirmButtonText: 'OK' });
            return;
          }
          self.loadPaymentSetupCodesWithActivity();
          Swal.fire({
            icon: 'success',
            title: 'Link cancelled',
            text: 'The payment method setup link is no longer valid.',
            confirmButtonText: 'OK'
          });
        },
        function (err) {
          self.paymentMethodSetupCodeCancelingCode = '';
          const msg = err && err.error && err.error.data
            ? String(err.error.data)
            : 'Failed to cancel setup link.';
          Swal.fire({ icon: 'error', title: 'Cancel failed', text: msg, confirmButtonText: 'OK' });
        }
      );
    });
  }

  private logPaymentMethodSetupLinkCreate(
    code?: string,
    link?: string,
    success?: boolean,
    errorMessage?: string
  ) {
    this.invoiceService.logPaymentMethodSetupLinkCreate({
      pharmacyId: this.pharmacyId,
      facilityId: this.facilityId,
      patientId: this.patientId,
      code: code ? code : undefined,
      paymentMethodSetupLink: link ? link : undefined,
      performedByUserId: this.sharedService.getUserId(),
      success: !!success,
      errorMessage: errorMessage ? errorMessage : undefined
    }).subscribe(
      function () { },
      function () { }
    );
  }

  /**
   * Setup-code timestamps are UTC. Action-log CreatedDate is Toronto local already.
   */
  formatEasternDateTime(value: string, treatAsUtc: boolean): string {
    if (!value) {
      return '—';
    }
    const text = String(value);
    if (!treatAsUtc) {
      if (text.indexOf('T') > 0 && text.length >= 16) {
        return text.substring(0, 10) + ' ' + text.substring(11, 16);
      }
    }

    let iso = text;
    if (treatAsUtc && iso.indexOf('Z') < 0 && iso.indexOf('+') < 0 && iso.indexOf('-', 10) < 0) {
      iso = iso.replace(' ', 'T');
      if (iso.charAt(iso.length - 1) !== 'Z') {
        iso = iso + 'Z';
      }
    }

    const d = new Date(iso);
    if (isNaN(d.getTime())) {
      return '—';
    }

    try {
      const formatted = d.toLocaleString('en-CA', {
        timeZone: 'America/Toronto',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
      return String(formatted).replace(',', '');
    } catch (e) {
      const pad = function (n: number) {
        return n < 10 ? '0' + n : String(n);
      };
      return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate())
        + ' ' + pad(d.getHours()) + ':' + pad(d.getMinutes());
    }
  }

  getActionLogLabel(actionCode: string): string {
    if (actionCode === 'PAYMENT_METHOD_SETUP_LINK_EMAIL_SEND') {
      return 'Email sent';
    }
    if (actionCode === 'PAYMENT_METHOD_SETUP_LINK_SMS_SEND') {
      return 'SMS sent';
    }
    if (actionCode === 'PAYMENT_METHOD_SETUP_LINK_CREATE') {
      return 'Created in portal';
    }
    if (actionCode === 'PAYMENT_METHOD_SETUP_LINK_CANCEL') {
      return 'Cancelled';
    }
    if (actionCode === 'PAYMENT_METHOD_SETUP_LINK_COMPLETE') {
      return 'Completed';
    }
    if (actionCode === 'PAYMENT_METHOD_CARD_DELETE') {
      return 'Card deleted';
    }
    if (actionCode === 'PAYMENT_METHOD_BANK_ACCOUNT_DELETE') {
      return 'Bank account deleted';
    }
    return actionCode || 'Activity';
  }

  getActionLogUserDisplay(row: any, setupCodeRow?: any): string {
    if (!row) {
      return '—';
    }

    // Completed setup: show client audit only (IP / short UA). Link may be used by
    // a family member or other payer, so avoid a fixed "Patient" label.
    if (row.actionCode === 'PAYMENT_METHOD_SETUP_LINK_COMPLETE') {
      const ip = this.getActionAuditIp(row, setupCodeRow);
      const uaShort = this.shortenUserAgent(this.getActionAuditUserAgent(row, setupCodeRow));
      const parts: string[] = [];
      if (ip) {
        parts.push(ip);
      }
      if (uaShort) {
        parts.push(uaShort);
      }
      if (parts.length) {
        return parts.join(' · ');
      }
      return '—';
    }

    if (row.userDisplayName) {
      return String(row.userDisplayName);
    }
    if (row.userId) {
      return String(row.userId);
    }
    return '—';
  }

  private getActionAuditIp(activity: any, setupCodeRow?: any): string {
    if (activity && activity.ipAddress) {
      return String(activity.ipAddress).trim();
    }
    if (setupCodeRow && setupCodeRow.lastValidatedIpAddress) {
      return String(setupCodeRow.lastValidatedIpAddress).trim();
    }
    return '';
  }

  private getActionAuditUserAgent(activity: any, setupCodeRow?: any): string {
    if (activity && activity.userAgent) {
      return String(activity.userAgent).trim();
    }
    if (setupCodeRow && setupCodeRow.lastValidatedUserAgent) {
      return String(setupCodeRow.lastValidatedUserAgent).trim();
    }
    return '';
  }

  /**
   * Short label for Action Log, e.g. "Chrome · Windows" or "Safari · iPhone".
   */
  shortenUserAgent(userAgent: string): string {
    if (!userAgent) {
      return '';
    }
    const ua = String(userAgent);
    const browser = this.detectBrowserLabel(ua);
    const os = this.detectOsLabel(ua);
    if (browser && os) {
      return browser + ' · ' + os;
    }
    if (browser) {
      return browser;
    }
    if (os) {
      return os;
    }
    if (ua.length <= 40) {
      return ua;
    }
    return ua.substring(0, 37) + '...';
  }

  private detectBrowserLabel(ua: string): string {
    const lower = ua.toLowerCase();
    if (lower.indexOf('edg/') >= 0 || lower.indexOf('edge/') >= 0) {
      return 'Edge';
    }
    if (lower.indexOf('opr/') >= 0 || lower.indexOf('opera') >= 0) {
      return 'Opera';
    }
    if (lower.indexOf('chrome/') >= 0 && lower.indexOf('chromium') < 0) {
      return 'Chrome';
    }
    if (lower.indexOf('firefox/') >= 0) {
      return 'Firefox';
    }
    if (lower.indexOf('safari/') >= 0 && lower.indexOf('chrome/') < 0) {
      return 'Safari';
    }
    return '';
  }

  private detectOsLabel(ua: string): string {
    const lower = ua.toLowerCase();
    if (lower.indexOf('iphone') >= 0) {
      return 'iPhone';
    }
    if (lower.indexOf('ipad') >= 0) {
      return 'iPad';
    }
    if (lower.indexOf('android') >= 0) {
      return 'Android';
    }
    if (lower.indexOf('windows') >= 0) {
      return 'Windows';
    }
    if (lower.indexOf('mac os') >= 0 || lower.indexOf('macintosh') >= 0) {
      return 'macOS';
    }
    if (lower.indexOf('linux') >= 0) {
      return 'Linux';
    }
    return '';
  }

  getActionLogEmail(row: any): string {
    const detail = this.parseActionLogDetail(row);
    return detail && detail.email ? String(detail.email) : '';
  }

  getActionLogLink(row: any): string {
    const detail = this.parseActionLogDetail(row);
    if (detail && detail.link) {
      return String(detail.link).trim();
    }
    if (detail && detail.paymentMethodSetupLink) {
      return String(detail.paymentMethodSetupLink).trim();
    }
    return '';
  }

  getPaymentSetupOpenLink(row: any): string {
    if (!row) {
      return '';
    }

    // Prefer link built from current PaymentLinkUrl (list API), not Action Log URLs
    // that may still point at an old ngrok / local host.
    if (row.paymentMethodSetupLink) {
      return String(row.paymentMethodSetupLink).trim();
    }

    const activities = row.activities || [];
    for (let i = 0; i < activities.length; i++) {
      const link = this.getActionLogLink(activities[i]);
      if (link) {
        return link;
      }
    }

    return '';
  }

  canOpenPaymentMethodSetupLink(row: any): boolean {
    if (!row) {
      return false;
    }

    const status = this.getPaymentSetupStatusDisplay(row);
    if (status !== 'PENDING' && status !== 'ACTIVE') {
      return false;
    }

    return !!this.getPaymentSetupOpenLink(row);
  }

  /**
   * Status shown in Payment Link Share (DB status, with LIMIT_REACHED / EXPIRED overrides).
   */
  getPaymentSetupStatusDisplay(row: any): string {
    if (!row) {
      return '—';
    }

    const status = row.status ? String(row.status).toUpperCase() : '';
    if (status === 'COMPLETED' || row.isUsed) {
      return 'COMPLETED';
    }
    if (status === 'CANCELED' || row.isCanceled) {
      return 'CANCELED';
    }
    if (row.isExpired) {
      return 'EXPIRED';
    }

    const used = row.validateCount != null ? Number(row.validateCount) : 0;
    const limit = row.validateLimit != null ? Number(row.validateLimit) : 10;
    if (!isNaN(used) && !isNaN(limit) && used >= limit) {
      return 'LIMIT_REACHED';
    }

    if (status) {
      return status;
    }
    return 'PENDING';
  }

  getPaymentSetupStatusBadgeClass(row: any): string {
    const status = this.getPaymentSetupStatusDisplay(row);
    if (status === 'PENDING' || status === 'ACTIVE') {
      return 'bg-success';
    }
    if (status === 'COMPLETED') {
      return 'bg-primary';
    }
    if (status === 'LIMIT_REACHED') {
      return 'bg-warning text-dark';
    }
    if (status === 'EXPIRED' || status === 'CANCELED') {
      return 'bg-secondary';
    }
    return 'bg-secondary';
  }

  getVisiblePaymentCards(): any[] {
    const cards = this.patientPaymentMethods && this.patientPaymentMethods.cards
      ? this.patientPaymentMethods.cards
      : [];
    if (!cards || !cards.length) {
      return [];
    }
    if (this.showInactivePaymentCards) {
      return cards;
    }
    return cards.filter(function (card) {
      return card && card.isActive;
    });
  }

  getVisiblePaymentBankAccounts(): any[] {
    const banks = this.patientPaymentMethods && this.patientPaymentMethods.bankAccounts
      ? this.patientPaymentMethods.bankAccounts
      : [];
    if (!banks || !banks.length) {
      return [];
    }
    if (this.showInactivePaymentBankAccounts) {
      return banks;
    }
    return banks.filter(function (bank) {
      return bank && bank.isActive;
    });
  }

  openExistingPaymentMethodSetupLink(row: any) {
    const link = this.getPaymentSetupOpenLink(row);
    if (!link) {
      return;
    }

    this.openCenteredPopup(link, 'solvoPaymentMethodSetup', 520, 780);
  }

  private parseActionLogDetail(row: any): any {
    if (!row || !row.detailJson) {
      return null;
    }
    try {
      return typeof row.detailJson === 'string'
        ? JSON.parse(row.detailJson)
        : row.detailJson;
    } catch (e) {
      return null;
    }
  }

  private getActionLogCode(row: any): string {
    const detail = this.parseActionLogDetail(row);
    return detail && detail.code ? String(detail.code).trim() : '';
  }

  /** Template helper for Payment Link Share code column. */
  getActionLogCodeDisplay(row: any): string {
    return this.getActionLogCode(row);
  }

  private isPaymentLinkShareAction(actionCode: string): boolean {
    return actionCode === 'PAYMENT_METHOD_SETUP_LINK_EMAIL_SEND'
      || actionCode === 'PAYMENT_METHOD_SETUP_LINK_SMS_SEND'
      || actionCode === 'PAYMENT_METHOD_SETUP_LINK_CREATE'
      || actionCode === 'PAYMENT_METHOD_SETUP_LINK_CANCEL';
  }

  togglePaymentActionLogPanel() {
    this.showPaymentActionLogPanel = !this.showPaymentActionLogPanel;
  }

  private mergePaymentSetupCodesWithActivity(codes: any[], logs: IUserActionLogRowVo[]): any[] {
    const list = codes && Array.isArray(codes) ? codes.slice() : [];
    const activitiesByCode: { [code: string]: any[] } = {};
    const unmatched: any[] = [];

    for (let i = 0; i < (logs || []).length; i++) {
      const log = logs[i];
      if (!log || !this.isPaymentLinkShareAction(log.actionCode)) {
        continue;
      }
      const code = this.getActionLogCode(log);
      if (!code) {
        unmatched.push(log);
        continue;
      }
      if (!activitiesByCode[code]) {
        activitiesByCode[code] = [];
      }
      activitiesByCode[code].push(log);
    }

    for (let i = 0; i < list.length; i++) {
      const row = list[i];
      const code = row && row.code ? String(row.code) : '';
      const activities = code && activitiesByCode[code]
        ? activitiesByCode[code].slice()
        : [];

      // Keep lifecycle events visible even when UserActionLog has no matching row.
      if (row && (row.isUsed || (row.status && String(row.status).toUpperCase() === 'COMPLETED'))) {
        if (!this.activitiesIncludeAction(activities, 'PAYMENT_METHOD_SETUP_LINK_COMPLETE')) {
          activities.push({
            actionCode: 'PAYMENT_METHOD_SETUP_LINK_COMPLETE',
            createdDate: row.completedAt || row.updatedAt || null,
            ipAddress: row.lastValidatedIpAddress || null,
            userAgent: row.lastValidatedUserAgent || null,
            success: true,
            treatCreatedDateAsUtc: true
          });
        }
      }
      if (row && (row.isCanceled || (row.status && String(row.status).toUpperCase() === 'CANCELED'))) {
        if (!this.activitiesIncludeAction(activities, 'PAYMENT_METHOD_SETUP_LINK_CANCEL')) {
          activities.push({
            actionCode: 'PAYMENT_METHOD_SETUP_LINK_CANCEL',
            createdDate: row.canceledAt || row.updatedAt || null,
            userDisplayName: '—',
            success: true,
            treatCreatedDateAsUtc: true
          });
        }
      }

      this.enrichCompleteActivityAudit(activities, row);
      row.activities = activities;
    }

    this.paymentSetupUnmatchedActionLogs = unmatched;
    return list;
  }

  private activitiesIncludeAction(activities: any[], actionCode: string): boolean {
    if (!activities || !activities.length || !actionCode) {
      return false;
    }
    for (let i = 0; i < activities.length; i++) {
      if (activities[i] && activities[i].actionCode === actionCode) {
        return true;
      }
    }
    return false;
  }

  private enrichCompleteActivityAudit(activities: any[], setupCodeRow: any): void {
    if (!activities || !activities.length || !setupCodeRow) {
      return;
    }
    for (let i = 0; i < activities.length; i++) {
      const act = activities[i];
      if (!act || act.actionCode !== 'PAYMENT_METHOD_SETUP_LINK_COMPLETE') {
        continue;
      }
      if (!act.ipAddress && setupCodeRow.lastValidatedIpAddress) {
        act.ipAddress = setupCodeRow.lastValidatedIpAddress;
      }
      if (!act.userAgent && setupCodeRow.lastValidatedUserAgent) {
        act.userAgent = setupCodeRow.lastValidatedUserAgent;
      }
    }
  }

  private splitPaymentActionLogs(logs: IUserActionLogRowVo[]) {
    const share: IUserActionLogRowVo[] = [];
    const all: IUserActionLogRowVo[] = [];
    for (let i = 0; i < (logs || []).length; i++) {
      const log = logs[i];
      if (!log) {
        continue;
      }
      all.push(log);
      if (this.isPaymentLinkShareAction(log.actionCode)) {
        share.push(log);
      }
    }
    this.paymentLinkShareLogs = share;
    this.paymentActionLogs = all;
  }

  loadPatientPaymentMethods(sync?: boolean) {
    if (this.pharmacyId == null || isNaN(this.pharmacyId)
      || this.patientId == null || isNaN(this.patientId)) {
      return;
    }

    const self = this;
    this.patientPaymentMethodsLoading = true;
    this.patientPaymentMethodsError = '';

    this.invoiceService.getPatientPaymentMethods({
      pharmacyId: this.pharmacyId,
      patientId: this.patientId,
      sync: !!sync
    }).subscribe(
      function (result) {
        self.patientPaymentMethodsLoading = false;
        if (result && result.success && result.data) {
          self.patientPaymentMethods = result.data;
          self.patientPaymentMethodsError = '';
        } else {
          self.patientPaymentMethods = null;
          self.patientPaymentMethodsError = (result && result.data)
            ? String(result.data)
            : 'Failed to load payment methods.';
        }
      },
      function () {
        self.patientPaymentMethodsLoading = false;
        self.patientPaymentMethods = null;
        self.patientPaymentMethodsError = 'Failed to load payment methods.';
      }
    );
  }

  formatCardNumberDisplay(cardF6L4: string): string {
    if (!cardF6L4) {
      return '—';
    }
    const raw = String(cardF6L4).replace(/\s/g, '');
    // SolvoPayment already returns masked values (e.g. ******9130).
    if (raw.indexOf('*') >= 0 || raw.indexOf('\u2022') >= 0) {
      return raw;
    }
    if (raw.length >= 4) {
      return '******' + raw.substring(raw.length - 4);
    }
    return raw;
  }

  /**
   * Escape then emphasize email / patient Id / Recipient / Code values in Action Summary.
   */
  formatActionSummaryHtml(summary: string): string {
    if (!summary) {
      return '—';
    }
    let s = this.escapeHtml(String(summary));
    s = s.replace(/(patient Id\s+)(\d+)/gi, '$1<strong>$2</strong>');
    s = s.replace(/(Code:\s*)([A-Za-z0-9-]+)/gi, '$1<strong>$2</strong>');
    s = s.replace(/(Recipient:\s*)([^.<]+)/gi, '$1<strong>$2</strong>');
    s = s.replace(
      /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g,
      '<strong>$1</strong>'
    );
    // Phone-like tokens (E.164 or national), skip when already inside <strong>.
    s = s.replace(
      /(\+?\d[\d\s().-]{7,}\d)/g,
      '<strong>$1</strong>'
    );
    // Avoid nested <strong> when Recipient already wrapped an email.
    s = s.replace(/<strong><strong>/g, '<strong>').replace(/<\/strong><\/strong>/g, '</strong>');
    return s;
  }

  onPaymentSetupCodesSort(field: string): void {
    if (!field) {
      return;
    }
    if (this.paymentSetupCodesSortField === field) {
      this.paymentSetupCodesSortDir = this.paymentSetupCodesSortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.paymentSetupCodesSortField = field;
      this.paymentSetupCodesSortDir = field === 'createdAt' || field === 'expiresAt' || field === 'lastValidatedAt'
        ? 'desc'
        : 'asc';
    }
    this.paymentSetupCodesPage = 1;
  }

  onPaymentActivitySort(field: string): void {
    if (!field) {
      return;
    }
    if (this.paymentActivitySortField === field) {
      this.paymentActivitySortDir = this.paymentActivitySortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.paymentActivitySortField = field;
      this.paymentActivitySortDir = field === 'createdDate' ? 'desc' : 'asc';
    }
    this.paymentActivityPage = 1;
  }

  getSortedPaymentSetupCodes(): any[] {
    const list = this.paymentMethodSetupCodes && this.paymentMethodSetupCodes.length
      ? this.paymentMethodSetupCodes.slice()
      : [];
    const field = this.paymentSetupCodesSortField || 'createdAt';
    const dir = this.paymentSetupCodesSortDir === 'asc' ? 1 : -1;
    const self = this;
    list.sort(function (a, b) {
      const va = self.getPaymentSetupSortValue(a, field);
      const vb = self.getPaymentSetupSortValue(b, field);
      if (va < vb) {
        return -1 * dir;
      }
      if (va > vb) {
        return 1 * dir;
      }
      return 0;
    });
    return list;
  }

  getPagedPaymentSetupCodes(): any[] {
    const sorted = this.getSortedPaymentSetupCodes();
    return this.slicePaymentLogPage(sorted, this.paymentSetupCodesPage);
  }

  getPaymentSetupCodesTotalPages(): number {
    const count = this.paymentMethodSetupCodes ? this.paymentMethodSetupCodes.length : 0;
    return this.getPaymentLogTotalPages(count);
  }

  getSortedPaymentActionLogs(): IUserActionLogRowVo[] {
    const list = this.paymentActionLogs && this.paymentActionLogs.length
      ? this.paymentActionLogs.slice()
      : [];
    const field = this.paymentActivitySortField || 'createdDate';
    const dir = this.paymentActivitySortDir === 'asc' ? 1 : -1;
    const self = this;
    list.sort(function (a, b) {
      const va = self.getPaymentActivitySortValue(a, field);
      const vb = self.getPaymentActivitySortValue(b, field);
      if (va < vb) {
        return -1 * dir;
      }
      if (va > vb) {
        return 1 * dir;
      }
      return 0;
    });
    return list;
  }

  getPagedPaymentActionLogs(): IUserActionLogRowVo[] {
    const sorted = this.getSortedPaymentActionLogs();
    return this.slicePaymentLogPage(sorted, this.paymentActivityPage);
  }

  getPaymentActivityTotalPages(): number {
    const count = this.paymentActionLogs ? this.paymentActionLogs.length : 0;
    return this.getPaymentLogTotalPages(count);
  }

  onPaymentSetupCodesPageChange(page: number): void {
    const total = this.getPaymentSetupCodesTotalPages();
    if (page < 1 || page > total) {
      return;
    }
    this.paymentSetupCodesPage = page;
  }

  onPaymentActivityPageChange(page: number): void {
    const total = this.getPaymentActivityTotalPages();
    if (page < 1 || page > total) {
      return;
    }
    this.paymentActivityPage = page;
  }

  /**
   * Pad activity lines so every Payment Link Share row keeps a stable height.
   */
  getPaymentSetupActivitySlots(row: any): any[] {
    const acts = row && row.activities && row.activities.length
      ? row.activities.slice()
      : [];
    const minSlots = 3;
    while (acts.length < minSlots) {
      acts.push(null);
    }
    return acts;
  }

  private getPaymentLogTotalPages(count: number): number {
    if (this.paymentLogPageSize <= 0 || count <= 0) {
      return 1;
    }
    return Math.max(1, Math.ceil(count / this.paymentLogPageSize));
  }

  private slicePaymentLogPage(list: any[], page: number): any[] {
    if (!list || !list.length) {
      return [];
    }
    const size = this.paymentLogPageSize;
    const safePage = page > 0 ? page : 1;
    const start = (safePage - 1) * size;
    return list.slice(start, start + size);
  }

  private getPaymentSetupSortValue(row: any, field: string): any {
    if (!row) {
      return '';
    }
    if (field === 'createdAt' || field === 'expiresAt' || field === 'lastValidatedAt') {
      if (!row[field]) {
        return 0;
      }
      const t = new Date(row[field]).getTime();
      return isNaN(t) ? 0 : t;
    }
    if (field === 'isUsed') {
      return row.isUsed ? 1 : 0;
    }
    if (field === 'status') {
      return this.getPaymentSetupStatusDisplay(row).toLowerCase();
    }
    if (field === 'validateCount') {
      return row.validateCount != null ? Number(row.validateCount) : 0;
    }
    if (field === 'code') {
      return row.code ? String(row.code).toLowerCase() : '';
    }
    return row[field] != null ? String(row[field]).toLowerCase() : '';
  }

  private getPaymentActivitySortValue(row: any, field: string): any {
    if (!row) {
      return '';
    }
    if (field === 'createdDate') {
      if (!row.createdDate) {
        return 0;
      }
      const t = new Date(row.createdDate).getTime();
      return isNaN(t) ? 0 : t;
    }
    if (field === 'success') {
      return row.success ? 1 : 0;
    }
    if (field === 'actionCode') {
      return this.getActionLogLabel(row.actionCode).toLowerCase();
    }
    if (field === 'actionSummary') {
      return row.actionSummary ? String(row.actionSummary).toLowerCase() : '';
    }
    if (field === 'user') {
      return this.getActionLogUserDisplay(row).toLowerCase();
    }
    return row[field] != null ? String(row[field]).toLowerCase() : '';
  }

  formatBankAccountDisplay(last4: string): string {
    if (!last4) {
      return '—';
    }
    const raw = String(last4).replace(/\s/g, '');
    // Prefer API-masked values when present; otherwise mask last-4 locally.
    if (raw.indexOf('*') >= 0 || raw.indexOf('\u2022') >= 0) {
      return raw;
    }
    return '******' + raw;
  }

  deletePaymentCard(card: any) {
    const customerId = this.patientPaymentMethods
      && this.patientPaymentMethods.customer
      && this.patientPaymentMethods.customer.id;
    if (!card || card.id == null || customerId == null) {
      return;
    }

    const self = this;
    const label = this.formatCardNumberDisplay(card.cardF6L4);
    Swal.fire({
      icon: 'warning',
      title: 'Delete card?',
      html: '<p>This removes the card from Helcim and marks it deleted in SolvoPayment.</p>'
        + '<p><strong>Card:</strong> ' + this.escapeHtml(label) + '</p>',
      showCancelButton: true,
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#dc3545'
    }).then(function (result) {
      if (!result || !result.value) {
        return;
      }
      self.paymentMethodDeletingKey = 'card-' + card.id;
      self.invoiceService.deletePatientPaymentCard({
        pharmacyId: self.pharmacyId,
        facilityId: self.facilityId,
        patientId: self.patientId,
        paymentCustomerId: Number(customerId),
        paymentMethodId: Number(card.id),
        externalCustomerId: self.patientPaymentMethods.customer.externalCustomerId
          ? String(self.patientPaymentMethods.customer.externalCustomerId)
          : undefined,
        externalCardId: card.externalCardId ? String(card.externalCardId) : undefined,
        externalBankAccountId: undefined,
        performedByUserId: self.sharedService.getUserId()
      }).subscribe(
        function (apiResult) {
          self.paymentMethodDeletingKey = '';
          if (!apiResult || !apiResult.success) {
            const msg = (apiResult && apiResult.data)
              ? String(apiResult.data)
              : 'Failed to delete card.';
            Swal.fire({ icon: 'error', title: 'Delete failed', text: msg, confirmButtonText: 'OK' });
            return;
          }
          self.loadPatientPaymentMethods(true);
          self.loadPaymentSetupCodesWithActivity();
          Swal.fire({
            icon: 'success',
            title: 'Card deleted',
            text: 'The card was removed.',
            confirmButtonText: 'OK'
          });
        },
        function (err) {
          self.paymentMethodDeletingKey = '';
          const msg = err && err.error && err.error.data
            ? String(err.error.data)
            : 'Failed to delete card.';
          Swal.fire({ icon: 'error', title: 'Delete failed', text: msg, confirmButtonText: 'OK' });
        }
      );
    });
  }

  deletePaymentBankAccount(bank: any) {
    const customerId = this.patientPaymentMethods
      && this.patientPaymentMethods.customer
      && this.patientPaymentMethods.customer.id;
    if (!bank || bank.id == null || customerId == null) {
      return;
    }

    const self = this;
    const label = this.formatBankAccountDisplay(bank.bankAccountNumberL4);
    Swal.fire({
      icon: 'warning',
      title: 'Delete bank account?',
      html: '<p>This removes the bank account from Helcim and marks it deleted in SolvoPayment.</p>'
        + '<p><strong>Account:</strong> ' + this.escapeHtml(label) + '</p>',
      showCancelButton: true,
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#dc3545'
    }).then(function (result) {
      if (!result || !result.value) {
        return;
      }
      self.paymentMethodDeletingKey = 'bank-' + bank.id;
      self.invoiceService.deletePatientPaymentBankAccount({
        pharmacyId: self.pharmacyId,
        facilityId: self.facilityId,
        patientId: self.patientId,
        paymentCustomerId: Number(customerId),
        paymentMethodId: Number(bank.id),
        externalCustomerId: self.patientPaymentMethods.customer.externalCustomerId
          ? String(self.patientPaymentMethods.customer.externalCustomerId)
          : undefined,
        externalCardId: undefined,
        externalBankAccountId: bank.externalBankAccountId
          ? String(bank.externalBankAccountId)
          : undefined,
        performedByUserId: self.sharedService.getUserId()
      }).subscribe(
        function (apiResult) {
          self.paymentMethodDeletingKey = '';
          if (!apiResult || !apiResult.success) {
            const msg = (apiResult && apiResult.data)
              ? String(apiResult.data)
              : 'Failed to delete bank account.';
            Swal.fire({ icon: 'error', title: 'Delete failed', text: msg, confirmButtonText: 'OK' });
            return;
          }
          self.loadPatientPaymentMethods(true);
          self.loadPaymentSetupCodesWithActivity();
          Swal.fire({
            icon: 'success',
            title: 'Bank account deleted',
            text: 'The bank account was removed.',
            confirmButtonText: 'OK'
          });
        },
        function (err) {
          self.paymentMethodDeletingKey = '';
          const msg = err && err.error && err.error.data
            ? String(err.error.data)
            : 'Failed to delete bank account.';
          Swal.fire({ icon: 'error', title: 'Delete failed', text: msg, confirmButtonText: 'OK' });
        }
      );
    });
  }

  viewPaymentBankAccountPad(bank: any) {
    const customerId = this.patientPaymentMethods
      && this.patientPaymentMethods.customer
      && this.patientPaymentMethods.customer.id;
    if (!bank || bank.id == null || customerId == null || this.paymentPadLoadingKey) {
      return;
    }

    const self = this;
    const label = this.formatBankAccountDisplay(bank.bankAccountNumberL4);
    this.paymentPadLoadingKey = 'bank-' + bank.id;

    Swal.fire({
      title: 'Loading PAD…',
      allowOutsideClick: false,
      allowEscapeKey: false,
      onOpen: function () {
        Swal.showLoading();
      }
    });

    this.invoiceService.getPatientPaymentBankAccountPad({
      paymentCustomerId: Number(customerId),
      bankAccountId: Number(bank.id)
    }).subscribe(
      function (apiResult) {
        self.paymentPadLoadingKey = '';
        if (!apiResult || !apiResult.success) {
          const msg = (apiResult && apiResult.data)
            ? String(apiResult.data)
            : 'Failed to load PAD.';
          Swal.fire({ icon: 'error', title: 'PAD lookup failed', text: msg, confirmButtonText: 'OK' });
          return;
        }
        Swal.fire({
          icon: 'info',
          title: 'PAD Agreement',
          html: self.buildPaymentBankPadHtml(label, apiResult.data),
          width: '44rem',
          confirmButtonText: 'OK'
        });
      },
      function (err) {
        self.paymentPadLoadingKey = '';
        const msg = err && err.error && err.error.data
          ? String(err.error.data)
          : 'Failed to load PAD.';
        Swal.fire({ icon: 'error', title: 'PAD lookup failed', text: msg, confirmButtonText: 'OK' });
      }
    );
  }

  private buildPaymentBankPadHtml(accountLabel: string, data: any): string {
    const pads = this.normalizePaymentPadList(data);
    let html = '<p class="mb-2 text-left"><strong>Account:</strong> '
      + this.escapeHtml(accountLabel || '—') + '</p>';

    html += '<p class="mb-3 text-left small" style="background:#fff3cd;border:1px solid #ffecb5;border-radius:0.25rem;padding:0.6rem 0.75rem;">'
      + '<strong>Payment readiness:</strong> '
      + 'Status, Active, Accepted, and Merchant Authorized must all be <strong>Yes</strong> '
      + 'before this bank account can be used for payment.'
      + '</p>';

    if (!pads || pads.length === 0) {
      html += '<p class="mb-1 text-muted text-left">No PAD agreement rows were recognized.</p>';
      html += '<pre class="text-left mb-2" style="font-size:0.75rem;white-space:pre-wrap;word-break:break-word;max-height:12rem;overflow:auto;">'
        + this.escapeHtml(this.stringifyPaymentPadPayload(data))
        + '</pre>';
      html += this.buildPaymentPadAuthorizationTermsHtml();
      return html;
    }

    for (let i = 0; i < pads.length; i++) {
      const pad = pads[i] || {};
      if (pads.length > 1) {
        html += '<p class="mb-1 mt-2 text-left"><strong>PAD ' + (i + 1) + '</strong></p>';
      }
      html += '<table class="table table-sm table-bordered mb-0 text-left" style="font-size:0.85rem;">'
        + this.buildPaymentPadRowHtml('External PAD ID', this.pickPaymentPadValue(pad, ['externalPadId', 'ExternalPadId', 'id', 'Id', 'padId', 'PadId']))
        + this.buildPaymentPadHtmlRow('Status', this.formatPaymentPadStatusYesNoBadge(this.pickPaymentPadValue(pad, ['status', 'Status'])))
        + this.buildPaymentPadHtmlRow('Active', this.formatPaymentPadYesNoBadge(this.pickPaymentPadValue(pad, ['isActive', 'IsActive'])))
        + this.buildPaymentPadHtmlRow('Accepted', this.formatPaymentPadYesNoBadge(this.pickPaymentPadValue(pad, ['isAccepted', 'IsAccepted', 'accepted', 'Accepted'])))
        + this.buildPaymentPadHtmlRow('Merchant Authorized', this.formatPaymentPadYesNoBadge(this.pickPaymentPadValue(pad, ['isMerchantAuthorized', 'IsMerchantAuthorized', 'merchantAuthorized', 'MerchantAuthorized'])))
        + this.buildPaymentPadRowHtml('Type', this.formatPaymentPadType(this.pickPaymentPadValue(pad, ['type', 'Type'])))
        + this.buildPaymentPadRowHtml('Date Created', this.pickPaymentPadValue(pad, ['dateCreated', 'DateCreated']))
        + this.buildPaymentPadRowHtml('Date Accepted', this.pickPaymentPadValue(pad, ['dateAccepted', 'DateAccepted']))
        + this.buildPaymentPadRowHtml('Date Earliest Debit', this.pickPaymentPadValue(pad, ['dateEarliestDebit', 'DateEarliestDebit']))
        + this.buildPaymentPadRowHtml('Date Revoked', this.pickPaymentPadValue(pad, ['dateRevoked', 'DateRevoked']))
        + this.buildPaymentPadRowHtml('Date Updated', this.pickPaymentPadValue(pad, ['dateUpdated', 'DateUpdated']))
        + '</table>';
    }

    html += this.buildPaymentPadAuthorizationTermsHtml();
    return html;
  }

  private buildPaymentPadAuthorizationTermsHtml(): string {
    const paragraphs = [
      'You authorize London Medical Pharmacy Advantage Care to debit this bank account for all amounts authorized by you in accordance with your agreement(s) with London Medical Pharmacy Advantage Care for goods or services.',
      'This agreement applies only to the method of payment between you and London Medical Pharmacy Advantage Care and this agreement and any termination of this agreement does not have any effect whatsoever with respect to any contract for goods or services between you and London Medical Pharmacy Advantage Care.',
      'All debits to your account will be administered by our payment services provider, Helcim Inc.',
      'You agree to waive your right to receive pre-notification of any amounts debited from your account and agree that you do not require advance notice of the amount of PADs before the debit is processed.',
      'You have certain recourse rights if any debit does not comply with this agreement. For example, you have the right to receive reimbursement for any debit that is not authorized or is not consistent with this agreement. To obtain more information on your recourse rights, you may contact your financial institution or visit www.payments.ca.',
      'You may amend or cancel this authorization at any time by contacting London Medical Pharmacy Advantage Care. For a sample cancellation form or further information on cancelling a PAD agreement, please contact your financial institution or visit www.payments.ca.'
    ];

    let html = '<div class="mt-3 text-left">'
      + '<p class="mb-2"><strong>PAD Authorization Terms</strong></p>'
      + '<div style="max-height:14rem;overflow:auto;border:1px solid #dee2e6;border-radius:0.25rem;padding:0.75rem;background:#f8f9fa;font-size:0.8rem;line-height:1.45;">';

    for (let i = 0; i < paragraphs.length; i++) {
      html += '<p class="mb-2">' + this.escapeHtml(paragraphs[i]) + '</p>';
    }

    html += '</div></div>';
    return html;
  }

  private stringifyPaymentPadPayload(data: any): string {
    if (data == null) {
      return 'null';
    }
    if (typeof data === 'string') {
      return data;
    }
    try {
      return JSON.stringify(data, null, 2);
    } catch (e) {
      return String(data);
    }
  }

  private pickPaymentPadValue(pad: any, keys: string[]): any {
    if (!pad || !keys || keys.length === 0) {
      return null;
    }
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      if (pad[key] != null && pad[key] !== '') {
        return pad[key];
      }
    }
    return null;
  }

  private normalizePaymentPadList(data: any): any[] {
    if (data == null) {
      return [];
    }
    if (typeof data === 'string') {
      try {
        data = JSON.parse(data);
      } catch (e) {
        return [];
      }
    }
    if (Array.isArray(data)) {
      return data;
    }
    if (typeof data !== 'object') {
      return [];
    }

    const nestedKeys = [
      'pad', 'pads', 'Pad', 'Pads',
      'items', 'Items',
      'padAgreements', 'PadAgreements',
      'results', 'Results'
    ];
    for (let i = 0; i < nestedKeys.length; i++) {
      const nested = data[nestedKeys[i]];
      if (Array.isArray(nested)) {
        return nested;
      }
      if (nested && typeof nested === 'object') {
        return [nested];
      }
    }

    if (data.id != null || data.Id != null || data.padId != null || data.PadId != null
      || data.status != null || data.Status != null
      || data.accepted != null || data.Accepted != null) {
      return [data];
    }

    return [];
  }

  private buildPaymentPadRowHtml(label: string, value: any): string {
    const display = value == null || value === '' ? '—' : String(value);
    return '<tr><th style="width:40%;">' + this.escapeHtml(label) + '</th><td>'
      + this.escapeHtml(display) + '</td></tr>';
  }

  /** Row that already contains trusted HTML (e.g. badges). */
  private buildPaymentPadHtmlRow(label: string, htmlValue: string): string {
    return '<tr><th style="width:40%;">' + this.escapeHtml(label) + '</th><td>'
      + (htmlValue || '—') + '</td></tr>';
  }

  private buildPaymentPadBadge(text: string, badgeClass: string): string {
    return '<span class="badge ' + badgeClass + '">' + this.escapeHtml(text) + '</span>';
  }

  private formatPaymentPadStatusBadge(status: any): string {
    if (status == null || status === '') {
      return '—';
    }
    const n = Number(status);
    if (n === 1) {
      return this.buildPaymentPadBadge('Active', 'bg-success');
    }
    if (n === 2) {
      return this.buildPaymentPadBadge('Inactive', 'bg-secondary');
    }
    return this.buildPaymentPadBadge(String(status), 'bg-secondary');
  }

  /** Status for readiness checklist: 1 = Yes, otherwise No. */
  private formatPaymentPadStatusYesNoBadge(status: any): string {
    if (status == null || status === '') {
      return '—';
    }
    const n = Number(status);
    if (n === 1) {
      return this.buildPaymentPadBadge('Yes', 'bg-success');
    }
    return this.buildPaymentPadBadge('No', 'bg-secondary');
  }

  private formatPaymentPadAcceptedBadge(accepted: any): string {
    if (accepted == null || accepted === '') {
      return '—';
    }
    if (accepted === true || accepted === 'true' || Number(accepted) === 1) {
      return this.buildPaymentPadBadge('Accepted', 'bg-success');
    }
    if (accepted === false || accepted === 'false' || Number(accepted) === 0) {
      return this.buildPaymentPadBadge('Not accepted', 'bg-warning text-dark');
    }
    return this.buildPaymentPadBadge(String(accepted), 'bg-secondary');
  }

  private formatPaymentPadYesNoBadge(value: any): string {
    if (value == null || value === '') {
      return '—';
    }
    if (value === true || value === 'true' || Number(value) === 1) {
      return this.buildPaymentPadBadge('Yes', 'bg-success');
    }
    if (value === false || value === 'false' || Number(value) === 0) {
      return this.buildPaymentPadBadge('No', 'bg-secondary');
    }
    return this.buildPaymentPadBadge(String(value), 'bg-secondary');
  }

  private formatPaymentPadType(type: any): string {
    if (type == null || type === '') {
      return '—';
    }
    const n = Number(type);
    if (n === 1) {
      return '1 (Personal)';
    }
    if (n === 2) {
      return '2 (Business)';
    }
    return String(type);
  }

  loadPaymentSetupCodesWithActivity() {
    if (this.pharmacyId == null || isNaN(this.pharmacyId)
      || this.facilityId == null || isNaN(this.facilityId)
      || this.patientId == null || isNaN(this.patientId)) {
      return;
    }

    this.paymentMethodSetupCodesLoading = true;
    this.paymentSetupLinkActionLogLoading = true;
    this.paymentMethodSetupCodesError = '';
    this.paymentSetupLinkActionLogError = '';
    this.paymentLinkShareLogs = [];
    this.paymentActionLogs = [];

    const self = this;
    forkJoin([
      this.invoiceService.getPaymentMethodSetupCodes({
        pharmacyId: this.pharmacyId,
        facilityId: this.facilityId,
        patientId: this.patientId
      }),
      this.invoiceService.searchUserActionLogs({
        pharmacyId: this.pharmacyId,
        facilityId: this.facilityId,
        patientId: this.patientId,
        actionCodes: [
          'PAYMENT_METHOD_SETUP_LINK_EMAIL_SEND',
          'PAYMENT_METHOD_SETUP_LINK_SMS_SEND',
          'PAYMENT_METHOD_SETUP_LINK_CREATE',
          'PAYMENT_METHOD_SETUP_LINK_CANCEL',
          'PAYMENT_METHOD_CARD_DELETE',
          'PAYMENT_METHOD_BANK_ACCOUNT_DELETE'
        ],
        maxRows: 200
      })
    ]).subscribe(
      function (results) {
        self.paymentMethodSetupCodesLoading = false;
        self.paymentSetupLinkActionLogLoading = false;

        const codesResult = results[0];
        const logs = results[1];
        const logRows = logs && Array.isArray(logs) ? logs : [];
        self.paymentSetupLinkActionLogs = logRows;
        self.splitPaymentActionLogs(logRows);

        if (!codesResult || !codesResult.success) {
          self.paymentMethodSetupCodesError = (codesResult && codesResult.data)
            ? String(codesResult.data)
            : 'Could not load setup codes.';
          self.paymentMethodSetupCodes = [];
          return;
        }

        self.paymentMethodSetupCodesError = '';
        const codes = codesResult.data && Array.isArray(codesResult.data) ? codesResult.data : [];
        self.paymentMethodSetupCodes = self.mergePaymentSetupCodesWithActivity(codes, logRows);
        self.paymentSetupCodesPage = 1;
        self.paymentActivityPage = 1;
        self.paymentSetupCodesSortField = 'createdAt';
        self.paymentSetupCodesSortDir = 'desc';
        self.paymentActivitySortField = 'createdDate';
        self.paymentActivitySortDir = 'desc';
      },
      function () {
        self.paymentMethodSetupCodesLoading = false;
        self.paymentSetupLinkActionLogLoading = false;
        self.paymentMethodSetupCodesError = 'Could not load setup codes and activity.';
        self.paymentSetupLinkActionLogError = 'Could not load payment activity.';
        self.paymentMethodSetupCodes = [];
        self.paymentSetupLinkActionLogs = [];
        self.paymentLinkShareLogs = [];
        self.paymentActionLogs = [];
      }
    );
  }

  loadPaymentSetupLinkActionLogs() {
    this.loadPaymentSetupCodesWithActivity();
  }

  loadPaymentMethodSetupCodes() {
    this.loadPaymentSetupCodesWithActivity();
  }

  private updateAnnualScrollButtonVisibility() {
    const y = window.pageYOffset || document.documentElement.scrollTop || 0;
    const winH = window.innerHeight || document.documentElement.clientHeight;
    const docHeight = document.documentElement.scrollHeight;
    const isNearBottom = (y + winH) >= (docHeight - 400);

    const isAnnualActive = this.activeProfileTab === 'year-tax';

    this.showAnnualScrollTopButton = isAnnualActive && y > 600;
    this.showAnnualScrollBottomButton = isAnnualActive && !isNearBottom;
  }

  onTaxYearChange() {
    this.loadAnnualInvoicePaymentItemsByYear();
  }

  private roundMoney(value: number): number {
    return Math.round((Number(value) || 0) * 100) / 100;
  }

  /** Pre-tax from Sage balance forward. */
  private resolveAnnualInvoicePreTaxAmount(preTaxAmount: number | null): number {
    if (preTaxAmount != null && !isNaN(preTaxAmount)) {
      return this.roundMoney(preTaxAmount);
    }
    return 0;
  }

  /** HST from Sage balance forward; falls back to 13% of invoice amount when missing. */
  private resolveAnnualInvoiceTaxAmount(amount: number, taxAmount: number | null): number {
    if (taxAmount != null && !isNaN(taxAmount)) {
      return this.roundMoney(taxAmount);
    }
    return this.roundMoney(amount * 0.13);
  }

  loadAnnualInvoicePaymentItemsByYear() {
    if (this.selectedTaxYear == null) {
      this.annualInvoiceItems = [];
      this.annualInvoiceTotalPays = 0;
      this.annualInvoiceTotalHst = 0;
      this.annualInvoiceTotalInvoice = 0;
      this.annualInvoiceMonthlySummary = [];
      return;
    }

    if (this.pharmacyId == null || isNaN(this.pharmacyId)
      || this.facilityId == null || isNaN(this.facilityId)
      || this.patientId == null || isNaN(this.patientId)) {
      return;
    }

    this.annualInvoiceLoading = true;

    this.invoiceService.getInvoicePaymentItemsByYear(this.pharmacyId, this.facilityId, this.patientId, this.selectedTaxYear).subscribe(
      res => {
        if (!res || !res.success) {
          this.annualInvoiceItems = [];
          this.annualInvoiceTotalPays = 0;
          this.annualInvoiceTotalHst = 0;
          this.annualInvoiceTotalInvoice = 0;
          this.annualInvoiceDateBreak = [];
          this.annualInvoiceDateEnd = [];
          this.annualInvoiceMonthlySummary = [];
          this.annualInvoiceLoading = false;
          this.setDefaultErrorHandler(res ? res.data : null);
          return;
        }

        const allItems = res.data ? (res.data as any[]) : [];
        
        // Filter out rows where fillDate is null for the display list only
        this.annualInvoiceItems = allItems.filter(item => item && item.fillDate != null);

        // Add a visual separator when fillDate group changes
        this.annualInvoiceDateBreak = [];
        this.annualInvoiceDateEnd = [];
        let lastFillDateKey = '';
        for (let i = 0; i < this.annualInvoiceItems.length; i++) {
          const item = this.annualInvoiceItems[i];
          const key = this.datepipe.transform(item && item.fillDate ? item.fillDate : null, 'yyyy-MM-dd') || '';
          this.annualInvoiceDateBreak[i] = i > 0 && key !== lastFillDateKey;
          lastFillDateKey = key;
        }

        // Put separator line at the *end* of the previous date group (bottom border)
        for (let i = 0; i < this.annualInvoiceItems.length; i++) {
          const item = this.annualInvoiceItems[i];
          const key = this.datepipe.transform(item && item.fillDate ? item.fillDate : null, 'yyyy-MM-dd') || '';
          const next = (i + 1) < this.annualInvoiceItems.length ? this.annualInvoiceItems[i + 1] : null;
          const nextKey = this.datepipe.transform(next && next.fillDate ? next.fillDate : null, 'yyyy-MM-dd') || '';
          this.annualInvoiceDateEnd[i] = i < (this.annualInvoiceItems.length - 1) && key !== nextKey;
        }

        // Create distinct list of TransactionMonth + InvoiceNumber + amounts (use all items for monthly summary)
        const distinctInvoices = new Map<string, { transactionMonth: string, amount: number, preTaxAmount: number, taxAmount: number }>();

        for (let i = 0; i < allItems.length; i++) {
          const item = allItems[i];
          const transactionMonth = item && item.transactionMonth ? item.transactionMonth : '';
          const invoiceNumber = item && item.invoiceNumber ? item.invoiceNumber : '';
          const amount = item && item.amount != null ? Number(item.amount) : 0;
          const preTaxAmount = item && item.preTaxAmount != null ? Number(item.preTaxAmount) : null;
          const taxAmount = item && item.taxAmount != null ? Number(item.taxAmount) : null;

          if (transactionMonth && invoiceNumber) {
            const key = `${transactionMonth}|${invoiceNumber}`;
            if (!distinctInvoices.has(key)) {
              distinctInvoices.set(key, {
                transactionMonth,
                amount,
                preTaxAmount: this.resolveAnnualInvoicePreTaxAmount(preTaxAmount),
                taxAmount: this.resolveAnnualInvoiceTaxAmount(amount, taxAmount)
              });
            }
          }
        }

        // Group by TransactionMonth and sum amounts
        let monthlyMap: any = {};
        distinctInvoices.forEach((value) => {
          const monthKey = value.transactionMonth;
          if (monthKey) {
            if (!monthlyMap[monthKey]) {
              monthlyMap[monthKey] = { month: monthKey, invoiceTotal: 0, subTotal: 0, hst: 0 };
            }
            monthlyMap[monthKey].invoiceTotal += value.amount;
            monthlyMap[monthKey].subTotal += value.preTaxAmount;
            monthlyMap[monthKey].hst += value.taxAmount;
          }
        });

        const monthKeys = Object.keys(monthlyMap).sort();
        const monthlySummary: any[] = [];
        let totalSubTotal = 0;
        let totalHst = 0;
        let totalInvoice = 0;

        for (let i = 0; i < monthKeys.length; i++) {
          const k = monthKeys[i];
          const m = monthlyMap[k];
          const invoiceTotal = Math.round((Number(m.invoiceTotal) || 0) * 100) / 100;
          const subTotal = Math.round((Number(m.subTotal) || 0) * 100) / 100;
          const hst = Math.round((Number(m.hst) || 0) * 100) / 100;

          monthlySummary.push({
            month: m.month,
            pays: subTotal,
            hst,
            invoiceTotal
          });
          totalSubTotal += subTotal;
          totalHst += hst;
          totalInvoice += invoiceTotal;
        }
        
        this.annualInvoiceMonthlySummary = monthlySummary;
        this.annualInvoiceTotalPays = Math.round(totalSubTotal * 100) / 100;
        this.annualInvoiceTotalHst = Math.round(totalHst * 100) / 100;
        this.annualInvoiceTotalInvoice = Math.round(totalInvoice * 100) / 100;

        this.annualInvoiceLoading = false;
      },
      error => {
        this.annualInvoiceItems = [];
        this.annualInvoiceTotalPays = 0;
        this.annualInvoiceTotalHst = 0;
        this.annualInvoiceTotalInvoice = 0;
        this.annualInvoiceDateBreak = [];
        this.annualInvoiceDateEnd = [];
        this.annualInvoiceMonthlySummary = [];
        this.annualInvoiceLoading = false;
        this.setDefaultErrorHandler(error && error.data ? error.data : null);
      }
    );
  }

  ngOnDestroy() {
    if (this.sageSearchSubscription) {
      this.sageSearchSubscription.unsubscribe();
    }
    this.sageSearchSubject.complete();
  }

  onSageSearchInput() {
    this.sageSearchSubject.next(this.sageSearchTerm);
  }

  getCustomerAddress(cust: ISageCustomerDto): string {
    if (!cust) return '';
    const parts = [cust.street1, cust.street2, cust.city, cust.province, cust.postalCode].filter(p => p && p.trim());
    return parts.join(', ') || '';
  }

  loadPatientInvoiceList() {
    this.invoiceService.getPatientInvoiceListWithBalanceForward(this.pharmacyId, this.facilityId, this.patientId).subscribe(
      res => {
        if (!res.success) {
          this.setDefaultErrorHandler(res.data);
        }

        this.invoiceList = res.data as IInvoiceNewVo[];
      },
      error => {
        this.setDefaultErrorHandler(error.data);
      });
  }

  loadData(displaySuccessMessage: boolean) {
    this.showLoadingMessage = true;

    const loadBalanceForward$: Observable<IResultObjVo<ISageBalanceForwardDto>> = this.invoiceService.getPatientBalanceForward(this.pharmacyId, this.facilityId, this.patientId).pipe(
      catchError(err => {
        this.setDefaultErrorHandler(err);
        return of(null);
      })
    );

    const loadPatientInvoiceList$: Observable<IResultObjVo<IInvoiceNewVo[]>> = this.invoiceService.getPatientInvoiceListWithBalanceForward(this.pharmacyId, this.facilityId, this.patientId).pipe(
      catchError(err => {
        this.setDefaultErrorHandler(err);
        return of(null);
      })
    );

    forkJoin([loadBalanceForward$, loadPatientInvoiceList$]).subscribe(([loadBalanceForwardResult, loadPatientInvoiceListResult]) => {
      if (loadBalanceForwardResult) {
        this.clearBalanceForwardExcludedRows();
        this.sageBalanceForward = loadBalanceForwardResult.data as ISageBalanceForwardDto;
        if (this.sageBalanceForward.transactionList && this.sageBalanceForward.transactionList.length > 0) {
          this.currentSortField = 'transactionDate';
          this.sortDirection = 'asc';
          this.sageBalanceForward.transactionList.sort((a, b) =>
            new Date(a.transactionDate).getTime() - new Date(b.transactionDate).getTime());
        }
        this.pruneBalanceForwardFilters();
      }
      if (this.balanceForwardAvailableYears.length > 0 && this.selectedTaxYear == null) {
        this.selectedTaxYear = this.balanceForwardAvailableYears[0];
        this.loadAnnualInvoicePaymentItemsByYear();
      }

      if (loadPatientInvoiceListResult) {
        this.invoiceList = loadPatientInvoiceListResult.data as IInvoiceNewVo[];
      }

      this.showLoadingMessage = false;

      if (displaySuccessMessage) {
        this.profileFromSageSuccessMessage();
      }
    });
  }

  openInvoice(invoiceId) {
    this.router.navigate([]).then(result => { window.open('report/invoice/' + invoiceId + '/' + 'Invoice', '_blank'); });
  }

  createSalesInvoice(invoice) {
    if (this.showLoadingMessage) return;

    this.invoiceService.getInvoiceSageProfile(this.pharmacyId, this.facilityId, this.patientId).subscribe(
      res => {
        const profile = res && res.data;
        if (profile == null || profile.sageCustomerId == null || profile.sageCustomerId === undefined) {
          Swal.fire({
            icon: 'warning',
            title: 'Update Sage Profile Required',
            text: 'Please update Sage Profile.',
          });
          return;
        }
        this.showCreateSalesInvoiceConfirm(invoice);
      },
      () => {
        Swal.fire({
          icon: 'warning',
          title: 'Update Sage Profile Required',
          text: 'Please update Sage Profile.',
        });
      }
    );
  }

  private showCreateSalesInvoiceConfirm(invoice) {
    Swal.fire({
      icon: 'question',
      title: 'Create New Invoice',
      html: `
        <div class="text-start">
          <p class="mb-3">Do you want to create a new invoice with the following details?</p>
          <div class="mb-3">
            <strong>Resident Name:</strong> ${invoice.billedTo}<br>
            <strong>Invoice ID:</strong> ${invoice.id}<br>
            <strong>Invoice Number:</strong> ${invoice.invoiceNumber}<br>
            <strong>Invoice Date:</strong> ${invoice.transactionDate}
          </div>
          <table class="table table-sm table-borderless mb-0">
            <tbody>
              <tr>
                <td>Taxable:</td>
                <td class="text-end">$${invoice.subTotalTaxableItems.toFixed(2)}</td>
              </tr>
              <tr>
                <td>Non-Taxable:</td>
                <td class="text-end">$${invoice.subTotalNonTaxableItems.toFixed(2)}</td>
              </tr>
              <tr>
                <td>HST:</td>
                <td class="text-end">$${invoice.hst.toFixed(2)}</td>
              </tr>
              <tr class="border-top">
                <td><strong>TOTAL:</strong></td>
                <td class="text-end"><strong>$${invoice.total.toFixed(2)}</strong></td>
              </tr>
            </tbody>
          </table>
        </div>
      `,
      showCancelButton: true,
      cancelButtonText: 'Cancel',
      confirmButtonText: 'Yes, create new invoice',
    }).then(result => {
      if(!result.isConfirmed) {
        return;
      }

      if (this.showLoadingMessage) {
        return;
      }

      this.showLoadingMessage = true;

      Swal.fire({
        title: 'Creating invoice...',
        text: 'This can take 10–20 seconds. Please wait.',
        allowOutsideClick: false,
        allowEscapeKey: false,
        onOpen: () => {
          Swal.showLoading();
        }
      });

      this.invoiceService.createSalesInvoice(this.pharmacyId, this.facilityId, this.patientId, invoice.id)
        // This endpoint can take 8–15+ seconds; keep a generous timeout
        .pipe(
          timeout(6000000),
          finalize(() => {
            // Ensure we always unblock the UI
            Swal.close();
            this.showLoadingMessage = false;
          })
        )
        .subscribe(
        res => {
          if (!res.success) {
            this.setDefaultErrorHandler(res.data);
            return;
          }
          this.profileFromSageSuccessMessage();
          this.loadPatientInvoiceList();
        },
        error => {
          this.setDefaultErrorHandler(error.data);
        }
      );
    });
  } 

  openInvoiceBatch() {
    if (!this.enablePrint) return;

    const selectedInvoiceList = this.invoiceList.filter(item => item.selected
      && item.invoiceReviewStatusId === 3
      && item.invoiceSageStatusId >= 3
      && item.total > 0);

    let invoiceIds = selectedInvoiceList.map(item => item.id);

    if (invoiceIds.length == 0) {
      Swal.fire({
        icon: 'warning',
        title: 'No invoice selected',
        text: 'Please select the invoice you wish to download.',
        confirmButtonText: 'OK'
      });
      return;
    }

    Swal.fire({
      icon: 'question',
      title: 'Download invoice PDF?',
      text: 'Do you want to download the invoice PDF file?',
      showCancelButton: true,
      cancelButtonText: 'Cancel',
      confirmButtonText: 'Yes, download',
    }).then(result => {
      if(!result.isConfirmed) {
        return;
      }

      this.showLoadingMessage = true;

      this.reportService.getInvoiceBatch(invoiceIds)
        .pipe(timeout(6000000))
        .subscribe(blob => {
          const fileUrl = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = fileUrl;
          const now = new Date();
          const formattedDate = now.getFullYear() +
          String(now.getMonth() + 1).padStart(2, '0') +
          String(now.getDate()).padStart(2, '0') +
          String(now.getHours()).padStart(2, '0') +
          String(now.getMinutes()).padStart(2, '0') +
          String(now.getMilliseconds()).padStart(2, '0');

          a.download = `InvoicesBatch_${formattedDate}.pdf`;
          a.click();
          window.URL.revokeObjectURL(fileUrl);
          this.showLoadingMessage = false;
        },
        error => {
          this.showLoadingMessage = false;
          this.loadErrorMessage("Oops! Something went wrong, please try again in a few minutes.");
        }
      )
    });    
  }

  onSendAllInvoicesByEmail() {
    if (!this.enablePrint) return;

    const selectedInvoiceList = this.invoiceList.filter(item => item.selected
      && item.invoiceReviewStatusId === 3
      && item.invoiceSageStatusId >= 3
      && item.total > 0);

    let invoiceIds = selectedInvoiceList.map(item => item.id);
    let invoiceNumbers = selectedInvoiceList.map(item => item.invoiceNumber);

    if (invoiceIds.length == 0) {
      Swal.fire({
        icon: 'warning',
        title: 'No invoice selected',
        text: 'Please select the invoice you wish to send the invoice email.',
        confirmButtonText: 'OK'
      });
      return;
    }

    Swal.fire({
      icon: 'question',
      title: 'Send invoice Email?',
      text: 'Are you sure you want to send the invoice email?',
      showCancelButton: true,
      cancelButtonText: 'Cancel',
      confirmButtonText: 'Yes, Send Invoice Email',
    }).then(result => {
      if(!result.isConfirmed) {
        return;
      }

      this.showLoadingMessage = true;

      this.invoiceService.sendAllInvoicesByEmail(this.sharedService.pharmacyId, this.sharedService.facilityId, invoiceIds)
        .pipe(timeout(6000000))
        .subscribe(data => {
          if (!data.success) {
            this.showLoadingMessage = false;
            this.loadErrorMessage("Oops! Something went wrong, please try again in a few minutes.");
            return;
          }
          this.showLoadingMessage = false;

          Swal.fire({
            icon: 'success',
            title: 'Invoice email sent',
            html: `
              <p>The invoice email has been sent successfully.</p>
              <p><strong>Invoice Numbers:</strong> ${invoiceNumbers}</p>
            `,
            confirmButtonText: 'OK'
          });
        },
        error => {
          this.showLoadingMessage = false;
          this.loadErrorMessage("Oops! Something went wrong, please try again in a few minutes.");
        }
      )
    })
  }

  selectAll(isSelected) {
    this.invoiceList.forEach(item => { item.selected = isSelected });
  }



  openRemoveConfirmModal() {
    this.closebutton.nativeElement.click();
    this.showLoadingMessage = false;
  }

  view(invoice) {
    if (!invoice || isNaN(invoice.id) || invoice.id <= 0) {
      return;
    }
    const self = this;
    this.activeProfileTab = 'patient-invoice-list';
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: 'invoices' },
      queryParamsHandling: 'merge',
      replaceUrl: true
    }).then(function () {
      self.router.navigate(['invoice/review-detail', invoice.id]);
    });
  }

  checkForInvoiceStatus(invoice): boolean {
    const VALID_INVOICE_REVIEW_STATUS = [3, 4, 5];
    return invoice && invoice.invoiceReviewStatusId === 3 && VALID_INVOICE_REVIEW_STATUS.indexOf(invoice.invoiceSageStatusId) >= 0;
  }

  isCurrentFacility(invoice: any): boolean {
    return invoice && invoice.facilityId === this.facilityId;
  }

  getInvoiceIssueDate(invoice: any): string {
    if (!invoice || !invoice.startDate) return '';
    var d = new Date(invoice.startDate);
    if (isNaN(d.getTime())) return '';
    d.setMonth(d.getMonth() + 1, 1);
    var y = d.getFullYear();
    var m = d.getMonth() + 1;
    var day = d.getDate();
    return y + '-' + (m < 10 ? '0' : '') + m + '-' + (day < 10 ? '0' : '') + day;
  }

  loadErrorMessage(message: any) {
    this.showErrorMessage = true;
    this.errorMessage = message != null ? String(message) : '';
    window.scroll(0, 0);
    setTimeout(() => {
      this.showErrorMessage = false;
    }, 5000);
  }

  clearErrorMessage() {
    this.showErrorMessage = false;
    this.errorMessage = "";
  }

  setDefaultErrorHandler(e) {
    console.error('Error: ', e);
    this.loadErrorMessage(formatOopsErrorMessage(e));
  }

  billingDateFormated(billableMonthAndYear): string {
    const billingDate = moment(billableMonthAndYear);
    return billingDate.format('YYYY-MM-DD');
  }

  onSorting(field: string, isProfile: boolean) {

    if (isProfile) return this.sortData(field, isProfile, this.sageBalanceForward.transactionList);

    return this.sortData(field, isProfile, this.invoiceList);
  }


  sortData(field: string, isProfile: boolean, list: any[]) {
    if (this.currentSortField === field) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.currentSortField = field;
      this.sortDirection = 'asc';
    }
    list.sort((a, b) => {
      const valueA = a[field];
      const valueB = b[field];

      // Handle dates
      const isDate = valueA instanceof Date || !isNaN(Date.parse(valueA));
      const aVal = isDate ? new Date(valueA).getTime() : valueA.toString().toLowerCase();
      const bVal = isDate ? new Date(valueB).getTime() : valueB.toString().toLowerCase();

      if (aVal < bVal) return this.sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return this.sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }


  async onGetProfileFromSage() {

    if (!this.sageBalanceForward.customer.sageName) {      
      this.loadErrorMessage("Please update the sage name first.");
      return;
    }

    this.showLoadingMessage = true;
    let result = await this.invoiceService.getProfileFromSage(this.pharmacyId, this.facilityId, this.patientId);
    if (!result.success) {
      this.setDefaultErrorHandler(result.data);
      this.showLoadingMessage = false;
      return;
    }
    
    this.loadData(true);
  }

  profileFromSageSuccessMessage() {
    this.displaySuccessMessage = true;
    this.successMessage = "The profile has been successfully retrieved from Sage.";
    setTimeout(() => {
      this.displaySuccessMessage = false;
    }, 5000);
  }

  loadPatientClinicalBanner(): void {
    if (isNaN(this.pharmacyId) || this.pharmacyId <= 0 || isNaN(this.patientId) || this.patientId <= 0) {
      return;
    }
    this.patientClinicalBannerReady = false;
    const self = this;
    forkJoin([
      this.patientService.getPatient(this.pharmacyId, this.patientId).pipe(catchError(function () { return of({}); })),
      this.residentService.getResidentProfileFor(this.pharmacyId, this.patientId).pipe(catchError(function () { return of({}); }))
    ]).subscribe(function (results) {
      self.patientDto = results[0] && typeof results[0] === 'object' ? results[0] : {};
      self.patientVo = results[1] && typeof results[1] === 'object' ? results[1] : {};
      self.patientClinicalBannerReady = true;
    });
  }

  copyToClipboard(text: string): void {
    if (!text || text === '—') {
      return;
    }
    const self = this;
    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      navigator.clipboard.writeText(text).then(function () {
        self.showCopiedToast(text);
      }).catch(function () {
        self.copyToClipboardFallback(text);
      });
    } else {
      this.copyToClipboardFallback(text);
    }
  }

  private showCopiedToast(text: string): void {
    Swal.fire({
      icon: 'success',
      title: 'Copied!',
      text: text,
      timer: 1500,
      showConfirmButton: false
    });
  }

  private copyToClipboardFallback(text: string): void {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      const copied = document.execCommand('copy');
      document.body.removeChild(ta);
      if (copied) {
        this.showCopiedToast(text);
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Copy failed',
          text: 'Could not copy text to clipboard.',
          confirmButtonText: 'OK'
        });
      }
    } catch (e) {
      Swal.fire({
        icon: 'error',
        title: 'Copy failed',
        text: 'Could not copy text to clipboard.',
        confirmButtonText: 'OK'
      });
    }
  }

  copyPatientIdToClipboard(): void {
    const idText = String(this.patientId);
    const self = this;
    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      navigator.clipboard.writeText(idText).then(function () {
        Swal.fire({
          icon: 'success',
          title: 'Copied',
          text: 'Patient ID was copied to the clipboard.',
          timer: 300,
          showConfirmButton: false
        });
      }).catch(function () {
        self.copyPatientIdFallback(idText);
      });
    } else {
      this.copyPatientIdFallback(idText);
    }
  }

  private copyPatientIdFallback(idText: string): void {
    try {
      const ta = document.createElement('textarea');
      ta.value = idText;
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      Swal.fire({
        icon: 'success',
        title: 'Copied',
        text: 'Patient ID was copied to the clipboard.',
        timer: 300,
        showConfirmButton: false
      });
    } catch (_e) {
      Swal.fire({
        icon: 'error',
        title: 'Copy failed',
        text: 'Could not copy Patient ID.',
        confirmButtonText: 'OK'
      });
    }
  }

  getPatientGenderText(): string {
    const dto = this.patientDto;
    if (dto && dto.genderText) {
      return String(dto.genderText);
    }
    const g = this.patientVo && this.patientVo.gender ? String(this.patientVo.gender) : '';
    if (g === 'M') {
      return 'Male';
    }
    if (g === 'F') {
      return 'Female';
    }
    return '';
  }

  getPatientBannerBirthdateText(): string {
    const dto = this.patientDto;
    if (dto && dto.birthdateText) {
      return String(dto.birthdateText);
    }
    const vo = this.patientVo;
    if (vo && vo.birthdate) {
      const m = moment(vo.birthdate);
      if (m.isValid()) {
        return m.format('YYYY-MM-DD');
      }
    }
    return '';
  }

  /** PMR label from Connect GetPatientDto (patGrpMast or patGrpMasts). */
  getPatientBannerPmrText(): string {
    const dto = this.patientDto;
    if (!dto) {
      return '';
    }
    const single = dto.patGrpMast != null ? String(dto.patGrpMast).trim() : '';
    if (single) {
      return single;
    }
    const list = dto.patGrpMasts;
    if (list && list.length) {
      const parts: string[] = [];
      for (let i = 0; i < list.length; i++) {
        const x = list[i];
        if (x != null && String(x).trim() !== '') {
          parts.push(String(x).trim());
        }
      }
      return parts.join(', ');
    }
    return '';
  }

  /** Same calendar rule as server <see cref="DateTimeHelper.GetAge" /> */
  getPatientInternationalAge(): number | null {
    const dto = this.patientDto;
    let birth: Date = null as any;
    if (dto && dto.birthdate) {
      const d = new Date(dto.birthdate);
      if (!isNaN(d.getTime())) {
        birth = d;
      }
    }
    if (!birth && this.patientVo && this.patientVo.birthdate) {
      const d2 = new Date(this.patientVo.birthdate);
      if (!isNaN(d2.getTime())) {
        birth = d2;
      }
    }
    if (!birth) {
      return null;
    }
    const today = new Date();
    const a = (today.getFullYear() * 100 + (today.getMonth() + 1)) * 100 + today.getDate();
    const b = (birth.getFullYear() * 100 + (birth.getMonth() + 1)) * 100 + birth.getDate();
    return Math.floor((a - b) / 10000);
  }

  getDietText(): string {
    const dto = this.patientDto && this.patientDto.diet ? String(this.patientDto.diet) : '';
    if (dto) {
      return dto;
    }
    const vo = this.patientVo && this.patientVo.diet ? String(this.patientVo.diet) : '';
    return vo;
  }

  getAllergiesText(): string {
    const dto = this.patientDto && this.patientDto.allergies ? String(this.patientDto.allergies) : '';
    if (dto) {
      return dto;
    }
    const vo = this.patientVo && this.patientVo.allergies ? String(this.patientVo.allergies) : '';
    return vo;
  }

  private metricPreferVo(voVal: string, dtoVal: string): string {
    const v = voVal != null ? String(voVal).trim() : '';
    if (v) {
      return v;
    }
    return dtoVal != null ? String(dtoVal).trim() : '';
  }

  private appendUnitIfMissing(value: string, unitToken: string, fullSuffix: string): string {
    const v = value != null ? String(value).trim() : '';
    if (!v) {
      return '';
    }
    const lower = v.toLowerCase();
    if (lower.indexOf(unitToken.toLowerCase()) >= 0) {
      return v;
    }
    return v + fullSuffix;
  }

  private formatMetricNumber(value: string, fractionDigits: number): string {
    const v = value != null ? String(value).trim() : '';
    if (!v) {
      return '';
    }
    const m = v.match(/-?\d+(\.\d+)?/);
    if (!m || m.length === 0) {
      return '';
    }
    const n = Number(m[0]);
    if (isNaN(n)) {
      return '';
    }
    return n.toFixed(fractionDigits);
  }

  private formatMetricWithUnit(value: string, fractionDigits: number, unitToken: string, unitSuffix: string): string {
    const num = this.formatMetricNumber(value, fractionDigits);
    if (!num) {
      return '';
    }
    return this.appendUnitIfMissing(num, unitToken, unitSuffix);
  }

  getMetricWeightDisplay(): string {
    const w = this.metricPreferVo(
      this.patientVo && this.patientVo.weight != null ? String(this.patientVo.weight) : '',
      this.patientDto && this.patientDto.weight != null ? String(this.patientDto.weight) : ''
    );
    return this.formatMetricWithUnit(w, 1, 'kg', ' kg');
  }

  getMetricHeightDisplay(): string {
    const h = this.patientVo && this.patientVo.height != null ? String(this.patientVo.height).trim() : '';
    return this.formatMetricWithUnit(h, 1, 'cm', ' cm');
  }

  getMetricCreatinineCrDisplay(): string {
    const c = this.metricPreferVo(
      this.patientVo && this.patientVo.creatinineCr != null ? String(this.patientVo.creatinineCr) : '',
      this.patientDto && this.patientDto.creatinineCr != null ? String(this.patientDto.creatinineCr) : ''
    );
    return this.formatMetricWithUnit(c, 0, 'mol', ' \u00B5mol/L');
  }

  getMetricCreatinineCrClDisplay(): string {
    const c = this.metricPreferVo(
      this.patientDto && this.patientDto.creatinineCrCl != null ? String(this.patientDto.creatinineCrCl) : '',
      this.patientVo && this.patientVo.creatinineCrCl != null ? String(this.patientVo.creatinineCrCl) : ''
    );
    return this.formatMetricWithUnit(c, 2, 'ml', ' mL/min');
  }

  private resolveCreatinineChartMoment(): any {
    const vo = this.patientVo;
    if (vo && vo.chartDate) {
      const m1 = moment(vo.chartDate);
      if (m1.isValid()) {
        return m1;
      }
    }
    if (vo && vo.chartDateText) {
      const m2 = moment(String(vo.chartDateText), ['YYYY-MM-DD', 'YYYY-MM-DDTHH:mm:ss'], true);
      if (m2.isValid()) {
        return m2;
      }
    }
    const dto = this.patientDto;
    if (dto && dto.chartDate) {
      const m3 = moment(String(dto.chartDate));
      if (m3.isValid()) {
        return m3;
      }
    }
    return null;
  }

  getCreatinineDateLine(): string {
    const m = this.resolveCreatinineChartMoment();
    if (!m || !m.isValid()) {
      return '';
    }
    const datePart = m.format('YYYY-MM-DD');
    const startChart = m.clone().startOf('day');
    const startToday = moment().startOf('day');
    const days = startToday.diff(startChart, 'days');
    const suffix = days === 0 ? '(today)' : ('(' + days + ' days ago)');
    return 'Creatinine Date: ' + datePart + ' ' + suffix;
  }

  onPatientProfileImageError(): void {
    if (this.patientProfileImageUrl !== this.patientProfileImageFallback) {
      this.patientProfileImageUrl = this.patientProfileImageFallback;
    }
  }

  private loadPatientProfileImageSettingsThenAvatar(): void {
    const self = this;
    this.patientService.getPatientProfileImageSettings().subscribe({
      next: function (cfg) {
        if (cfg && cfg.hashInputSalt && cfg.fileservicePatientsBaseUrl) {
          self.patientProfileImageSettings = cfg;
          self.updatePatientProfileImageUrl();
        }
      },
      error: function () { /* keep fallback */ }
    });
  }

  private updatePatientProfileImageUrl(): void {
    const cfg = this.patientProfileImageSettings;
    if (!cfg || !cfg.hashInputSalt || !cfg.fileservicePatientsBaseUrl) {
      return;
    }
    const pharmacyId = this.pharmacyId;
    let facilityId = this.facilityId;
    if (isNaN(facilityId)) {
      facilityId = 0;
    }
    const patientIdNum = this.patientId;
    const self = this;

    if (isNaN(pharmacyId) || pharmacyId <= 0 || isNaN(patientIdNum) || patientIdNum <= 0) {
      return;
    }

    if (typeof TextEncoder === 'undefined') {
      return;
    }

    const w = window as any;
    const cryptoRoot = w.crypto || w.msCrypto;
    if (!cryptoRoot || !cryptoRoot.subtle || typeof cryptoRoot.subtle.digest !== 'function') {
      return;
    }

    const input = pharmacyId + '_' + facilityId + '_' + patientIdNum + '_' + cfg.hashInputSalt;
    const enc = new TextEncoder();
    cryptoRoot.subtle.digest('SHA-1', enc.encode(input)).then(function (digest) {
      const hash = self.arrayBufferToHexLower(digest);
      const remoteUrl = cfg.fileservicePatientsBaseUrl +
        pharmacyId + '_' + facilityId + '_' + hash + '.jpg';
      self.applyPatientProfileImageIfLoadOk(remoteUrl);
    }).catch(function () { /* keep fallback */ });
  }

  private applyPatientProfileImageIfLoadOk(remoteUrl: string): void {
    const self = this;
    const probe = new Image();
    probe.onload = function () {
      self.patientProfileImageUrl = remoteUrl;
    };
    probe.onerror = function () { /* keep fallback */ };
    probe.src = remoteUrl;
  }

  private arrayBufferToHexLower(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let hex = '';
    for (let i = 0; i < bytes.length; i++) {
      let h = bytes[i].toString(16);
      if (h.length < 2) {
        hex += '0';
      }
      hex += h;
    }
    return hex;
  }


}
